import { normalizeDate } from "@app/utils/functions";
import {
  IFormConditioning,
  IFormSamplesInputs,
  KeysTabsAnalysis,
  LabelKeys,
  LabelTypesOptions,
  SampleRegistrationTypes,
} from "../../../types/samples.types";
import { mapNameToValue } from "../../../utils/tables/conditioning.tables";
import { mapsProcessingCategory } from "../../../utils/tables/processing.tables";
import dayjs from "dayjs";
import { formatDateOrNow } from "@app/utils/formatDate";
import { UserType } from "@app/types/genericTables.types";
import { useEquipment } from "@services/servicesGenericsTables";
import {
  idsAT,
  idsBTBR,
  idsBRISO,
  idsBRCurrier,
  idsBTISO,
  idsBTCurrier,
  idsBTBRCurrier,
  idsBTBRInSample,
  idsBTBRInSampleCurrier,
  idsCurries,
  idsDefault,
  idsDP,
  idsFe55Ni63,
  idsIodo,
  idsSR,
  idsPuAmCM,
  labelString,
} from "../components/analysisComponents/commonForms/resultsTable/labelsAnalysis";
import { TYPES_RECEPTION } from "../components/filterSample";
import { LABELS_TABLE_CALCINATION } from "../components/initialComponents/ProcessingSubforms/calcination/tableCalcination";
import { LABELS_TABLE_DRYING } from "../components/initialComponents/ProcessingSubforms/calcination/tableDrying";

export function filter(
  filters: IFormSamplesInputs,
  samplesList: SampleRegistrationTypes[]
) {
  const { date_from, date_to, is_reception } = filters;

  return samplesList.filter((sample) => {
    const sampleDate = new Date(
      sample.sampleManagement?.date_start_reception || ""
    );
    const normalizedSampleDate = normalizeDate(sampleDate);

    if (is_reception === TYPES_RECEPTION.NO) {
      if (sample.sampleManagement?.date_start_reception) {
        return false;
      }
    }

    if (is_reception === TYPES_RECEPTION.YES) {
      if (!sample.sampleManagement?.date_start_reception) {
        return false;
      }

      if (date_from && date_to) {
        const normalizedFrom = normalizeDate(date_from);
        const normalizedTo = normalizeDate(date_to);

        if (
          !(
            normalizedSampleDate >= normalizedFrom &&
            normalizedSampleDate <= normalizedTo
          )
        ) {
          return false;
        }
      } else if (date_from) {
        const normalizedFrom = normalizeDate(date_from);

        if (!(normalizedSampleDate >= normalizedFrom)) {
          return false;
        }
      } else if (date_to) {
        const normalizedTo = normalizeDate(date_to);

        if (!(normalizedSampleDate <= normalizedTo)) {
          return false;
        }
      }
    }

    return (
      (!filters.year || sample.year === Number(filters.year)) &&
      (!filters.client_reference ||
        sample.client_reference === filters.client_reference) &&
      (!filters.masl_reference ||
        sample.masl_reference === filters.masl_reference) &&
      (!filters.installation ||
        sample.installation.id === filters.installation.id) &&
      (!filters.analysis ||
        sample.analysis_type.some(
          (analysis) =>
            analysis.id === filters.analysis?.id &&
            analysis.codigo === filters.analysis.codigo
        )) &&
      (!filters.reason || sample.reason.id === filters.reason.id) &&
      (!filters.sampling_points ||
        sample.sampling_points.id === filters.sampling_points.id) &&
      (!filters.sample_type ||
        sample.sample_type.id === filters.sample_type.id) &&
      (filters.shared === undefined ||
        filters.shared === null ||
        sample.shared === filters.shared?.id) &&
      (!filters.accumulation ||
        sample.accumulation.id === filters.accumulation.id)
    );
  });
}

export const samplingMap = (sample: any) => {
  return {
    analyst: sample.analyst,
    date_start_sampling: sample.date_start_sampling,
    date_end_sampling: sample.date_end_sampling,
    week: sample.week,
    year: sample.year,
    amount_sample: sample.amount_sample,
    unit_types: sample.unit_types,
    s2_kg: sample.s2_kg,
    ll2_m: sample.ll2_m,
    m3_m2: sample.m3_m2,
    type_sampler: sample.type_sampler,
    masl_sampler: sample.masl_sampler,
    proc_take_sample: sample.proc_take_sample,
    sample_registration: sample.sample_registration,
    id: sample.idSampling,
    conservation: sample.conservation,
    product: sample.product,
    productTimestamp: sample.conservation ? sample.productTimestamp : null,
    filtration: sample.filtration,
    filter: sample.filter,
    filtrationTimestamp: sample.filtration ? sample.filtrationTimestamp : null,
    conservationFiltrationEntity: sample.conservationFiltrationEntity,
    samplingType: sample.samplingType,
    externalTakesSampleEntity: sample.externalTakesSampleEntity
      ? JSON.stringify({
          externalTakesSampleEntity: sample.externalTakesSampleEntity,
          external_sampler: sample.external_sampler,
        })
      : null,
    recipientPostalCode: sample.recipientPostalCode,
    client_reference: sample.client_reference,
  };
};

export const samplingMapKeys = [
  "analyst",
  "date_start_sampling",
  "date_end_sampling",
  "week",
  "year",
  "amount_sample",
  "unit_types",
  "s2_kg",
  "ll2_m",
  "m3_m2",
  "type_sampler",
  "masl_sampler",
  "proc_take_sample",
  "sample_registration",
  "external_sampler",
  "conservation",
  "product",
  "productTimestamp",
  "filtration",
  "filter",
  "filtrationTimestamp",
  "conservationFiltrationEntity",
  "samplingType",
  "externalTakesSampleEntity",
  "destinatario",
];

export function hasTrueValueInKeys(obj: any) {
  return samplingMapKeys.some((key) => obj[key] === true);
}

export function isEqualSampling(currentValue: any, getValues: any) {
  const requiredFieldsSampling: string[] = [
    "analyst",
    "date_start_sampling",
    "date_end_sampling",
    "week",
    "year",
    "amount_sample",
    "unit_types",
    "s2_kg",
    "ll2_m",
    "m3_m2",
    "type_sampler",
    "masl_sampler",
  ];

  const filteredCurrentValue = Object.keys(currentValue)
    .filter((key) => requiredFieldsSampling.includes(key))
    .reduce((obj, key) => {
      obj[key] = currentValue[key];
      return obj;
    }, {});

  for (let key of requiredFieldsSampling) {
    if (
      typeof filteredCurrentValue[key] === "object" &&
      filteredCurrentValue[key] !== null
    ) {
      if (filteredCurrentValue[key].id !== undefined) {
        if (
          !getValues[key] ||
          getValues[key].id === undefined ||
          filteredCurrentValue[key].id !== getValues[key].id
        ) {
          return false;
        }
      } else {
        if (!isEqualSampling(filteredCurrentValue[key], getValues[key])) {
          return false;
        }
      }
    } else if (
      typeof filteredCurrentValue[key] === "string" &&
      key.includes("date")
    ) {
      if (
        new Date(filteredCurrentValue[key]).getTime() !==
        new Date(getValues[key]).getTime()
      ) {
        return false;
      }
    } else {
      if (
        filteredCurrentValue[key] !== getValues[key] &&
        !(filteredCurrentValue[key] == null && getValues[key] == null)
      ) {
        return false;
      }
    }
  }

  return true;
}

export const managementMap = (sample: any) => {
  return {
    withdrawal: sample.reservation ? "recepción" : "reserva",
    reception_analyst: sample.reception_analyst,
    date_start_reception: sample.date_start_reception,
    malpma_procedure: sample.malpma_procedure,
    retirement_analyst: sample.retirement_analyst,
    retirement_date: sample.retirement_date,
    sample_registration: sample.sample_registration,
    reservation: sample.reservation,
    id: sample.idManagement,
  };
};

export const conditioningMap = (sample: any) => {
  const category = mapNameToValue[sample.category];
  const equipmentSelected = useEquipment
    .getState()
    .get(sample.conditioning_equipment)?.codigo;
  return {
    equipment: equipmentSelected,
    date_time: sample.conditioning_date_time,
    date_time_end: sample.conditioning_date_time_end,
    analyst: sample.conditioning_analyst,
    sample_amount_without_acidification:
      sample.sample_amount_without_acidification,
    sample_registration: sample.sample_registration,
    category: category,
    conditioning_procedure: sample.conditioning_processing_procedure,
    reagent: sample.conditioning_reagent,
    id: sample.idConditioning,
  };
};

export const processingMap = (sample: any) => {
  return {
    processing_procedure: sample.processing_procedure,
    analytical_balance: sample.analytical_balance,
    equipment: sample.processing_equipment,
    date_time: sample.processing_date_time_not_applicable,
    sample_quantity: sample.sample_quantity,
    analyst: sample.processing_analyst_not_applicable,
    date_time_end: sample.processing_date_time_end,
    wet_weight: sample.wet_weight,
    gross_weight: sample.gross_weight,
    reagent: sample.processing_reagent,
    volume: sample.volume,
    processing_category: mapsProcessingCategory["not_applicable"],
    sample_registration: sample.sample_registration,
    id: sample.idProcessing,
  };
};

export const processingGrindingMap = (sample: any) => {
  return {
    analyst: sample.processing_analyst_grinding,
    processing_procedure: sample.processing_procedure_grinding,
    equipment: sample.processing_equipment_grinding,
    date_time: sample.processing_date_time_grinding,
    sample_quantity: Number(sample.sample_quantity_grinding),
    id: sample.idProcessing_grinding,
    processing_category: mapsProcessingCategory["grinding"],
    sample_registration: Number(sample.sample_registration),
  };
};

export const processingSiftingMap = (sample: any) => {
  return {
    analyst: sample.processing_analyst_sifting,
    processing_procedure: sample.processing_procedure_sifting,
    equipment: sample.analytical_balance_sifting,
    date_time: sample.processing_date_time_sifting,
    sample_quantity: sample.sample_quantity_sifting,
    id: sample.idProcessing_sifting,
    processing_category: mapsProcessingCategory["sifting"],
    wet_weight: sample.wet_weight_sifting,
    gross_weight: sample.gross_weight_sifting,
    sample_registration: Number(sample.sample_registration),
  };
};

export const processingEvaporationMap = (sample: any) => {
  return {
    analyst: sample.processing_analyst_evaporation,
    processing_procedure: sample.processing_procedure_evaporation,
    equipment: sample.processing_equipment_evaporation,
    date_time: sample.processing_date_time_evaporation,
    date_time_end: sample.processing_date_time_end_evaporation,
    id: sample.idProcessing_evaporation,
    processing_category: mapsProcessingCategory["evaporation"],
    sample_volume: sample.sample_volume_evaporation,
    reagent: sample.processing_reagent_evaporation,
    sample_registration: Number(sample.sample_registration),
  };
};

export const processingDigestionMap = (sample: any) => {
  return {
    analyst: sample.processing_analyst_digestion,
    processing_procedure: sample.processing_procedure_digestion,
    equipment: sample.processing_equipment_digestion,
    date_time: sample.processing_date_time_digestion,
    id: sample.idProcessing_digestion,
    processing_category: mapsProcessingCategory["digestion"],
    sample_quantity: sample.sample_quantity_digestion,
    sample_registration: Number(sample.sample_registration),
  };
};

export const processingDryingMap = (sample: any) => {
  return {
    analyst: sample.processing_analyst_drying,
    processing_procedure: sample.processing_procedure_drying,
    equipment: sample.processing_equipment_drying,
    date_time: sample.processing_date_time_drying,
    date_time_end: sample.processing_date_time_end_drying,
    id: sample.idProcessing_drying,
    processing_category: mapsProcessingCategory["drying"],
    capsules_number: sample.capsules_number_drying,
    sample_registration: Number(sample.sample_registration),
    capsules_number_old: sample.capsules_number_old,
    wet_dry: sample.wet_dry,
  };
};

export const processingCalcinationMap = (sample: any) => {
  return {
    analyst: sample.processing_analyst_calcination,
    processing_procedure: sample.processing_procedure_calcination,
    equipment: sample.processing_equipment_calcination,
    date_time: sample.processing_date_time_calcination,
    date_time_end: sample.processing_date_time_end_calcination,
    id: sample.idProcessing_calcination,
    processing_category: mapsProcessingCategory["calcination"],
    capsules_number: sample.capsules_number_calcination,
    sample_registration: Number(sample.sample_registration),
    capsules_number_old: sample.capsules_number_old,
    dry_calcination: sample.dry_calcination,
  };
};

export const processingChippingMap = (sample: any) => {
  return {
    analyst: sample.processing_analyst_chipping,
    processing_procedure: sample.processing_procedure_chipping,
    equipment: sample.processing_equipment_chipping,
    date_time: sample.processing_date_time_chipping,
    sample_quantity: Number(sample.sample_quantity_chipping),
    id: sample.idProcessing_chipping,
    processing_category: mapsProcessingCategory["chipping"],
    sample_registration: Number(sample.sample_registration),
  };
};

export const preparationMap = (sample: any) => {
  return {
    analyst: sample.analysis_analyst,
    preparation_procedure: sample.preparation_procedure,
    start_date: sample.start_date,
    equipment: sample.analysis_equipment,
    amount_sample: sample.analysis_amount_sample,
    planchette: sample.planchette,
    planchette_with_precipitate: sample.planchette_with_precipitate,
    carrier_concentration: sample.carrier_concentration,
    mg_torta: sample.mg_torta,
    unit_types: sample.analysis_unit_types,
    sample_registration: sample.sample_registration,
    mg_torta_start_limit: sample.mg_torta_start_limit,
    mg_torta_end_limit: sample.mg_torta_end_limit,
    analysis_type: sample.typeAnalysis,
    scintillator_lot_number: sample.scintillator_lot_number,
    valid_preparation: sample.valid_preparation,
    type: sample.typePreparation,
    id: sample.preparationId,
    processing: sample.processingId,
    state: 1,
    totalSampleWaterVolume: sample.totalSampleWaterVolume,
    sampleCountVapor: sample.sampleCountVapor,
    sampledAirVolume: sample.sampledAirVolume,
    dataVolumeRegistries: sample.dataVolumeRegistries,
    numberOfTraps: sample.numberOfTraps,
    sampledAirVolumeSilicagel: sample.numberOfTraps,
    dataTrampsTritioAirArrayBefore: sample.dataTrampsTritioAirArrayBefore,
    dataTrampsTritioAirArrayAfter: sample.dataTrampsTritioAirArrayAfter,
    analyzedAirVolume: sample.analyzedAirVolume,
    analyzedWaterVolume: sample.analyzedWaterVolume,
    typePreparation: sample.typePreparation,
    performance: sample.preparation_performance,
    yellow_precipitate_silver_nitrate:
      sample.yellow_precipitate_silver_nitrate?.value,
    amount_ashes: sample.amount_ashes,
    amount_equivalent: sample.amount_equivalent,
    start_date_precipitation: sample.start_date_precipitation,
    preparation_procedure_gross: sample.preparation_procedure_gross,
  };
};
export const preparationMapBRBT = (sample: any) => {
  return {
    sample_registration: sample.sample_registration,
    analysis_type: sample.typeAnalysis,
    id: sample.preparationId,
    processing: sample.processingId,
    state: 1,
    analyst: sample.analyst,
    preparation_procedure: sample.preparation_procedure,
    start_date: sample.start_date,
    amount_sample: sample.analysis_amount_sample,
    equipment: sample.analysis_equipment,
    unit_types: sample.analysis_unit_types,
    planchette: sample.planchette,
    planchette_with_precipitate_1: sample.planchette_with_precipitate_1,
    planchette_with_precipitate_2: sample.planchette_with_precipitate_2,
    planchette_with_precipitate_3: sample.planchette_with_precipitate_3,
    planchette_with_precipitate_4: sample.planchette_with_precipitate_4,
    planchette_with_precipitate_5: sample.planchette_with_precipitate_5,
    constant_weight: sample.constant_weight?.value,
    mg_torta: sample.mg_torta,
    planchette_with_precipitate: sample.planchette_with_precipitate,
    valid_preparation: sample.valid_preparation,
  };
};

export const preparationATMap = (sample: any) => {
  return {
    analyst: sample.analysis_analyst,
    preparation_procedure: sample.preparation_procedure,
    start_date: sample.start_date,
    equipment: sample.analysis_equipment,
    amount_sample: sample.analysis_amount_sample,
    planchette: sample.planchette,
    planchette_with_precipitate: sample.planchette_with_precipitate,
    mg_torta: sample.mg_torta,
    unit_types: sample.analysis_unit_types,
    sample_registration: sample.sample_registration,
    mg_torta_start_limit: sample.mg_torta_start_limit,
    mg_torta_end_limit: sample.mg_torta_end_limit,
    analysis_type: sample.typeAnalysis,
    scintillator_lot_number: sample.scintillator_lot_number,
    valid_preparation: sample.valid_preparation,
    type: sample.typePreparation,
    id: sample.preparationId,
    processing: sample.processingId,
    state: 1,
    totalSampleWaterVolume: sample.totalSampleWaterVolume,
    sampleCountVapor: sample.sampleCountVapor,
    sampledAirVolume: sample.sampledAirVolume,
    dataVolumeRegistries: sample.dataVolumeRegistries,
    numberOfTraps: sample.numberOfTraps,
    sampledAirVolumeSilicagel: sample.numberOfTraps,
    dataTrampsTritioAirArrayBefore: sample.dataTrampsTritioAirArrayBefore,
    dataTrampsTritioAirArrayAfter: sample.dataTrampsTritioAirArrayAfter,
    analyzedAirVolume: sample.analyzedAirVolume,
    analyzedWaterVolume: sample.analyzedWaterVolume,
    typePreparation: sample.typePreparation,
  };
};

export const preparationIodo = (sample: any) => {
  return {
    analyst: sample.analysis_analyst,
    preparation_procedure: sample.preparation_procedure,
    start_date: sample.start_date,
    end_date: sample.end_date,
    amount_sample: sample.analysis_amount_sample,
    equipment: sample.analysis_equipment,
    scintillator_lot_number: sample.scintillator_lot_number,
    carrier_concentration: sample.carrier_concentration,
    planchette: sample.planchette,
    planchette_with_precipitate: sample.planchette_with_precipitate,
    mg_torta: sample.mg_torta,
    performance: sample.preparation_performance,
    unit_types: sample.analysis_unit_types,
    yellow_precipitate_silver_nitrate:
      sample.yellow_precipitate_silver_nitrate?.value || null,
    violet_coloration: sample.violet_coloration?.value || null,
    sample_registration: sample.sample_registration,
    analysis_type: sample.typeAnalysis,
    id: sample.preparationId,
    processing: sample.processingId,
    state: 1,
  };
};

export const preparationMapBPP = (sample: any) => {
  return {
    sample_registration: sample.sample_registration,
    analysis_type: sample.typeAnalysis,
    id: sample.preparationId,
    processing: sample.processingId,
    state: 1,
    analyst: sample.analyst,
    preparation_procedure: sample.preparation_procedure,
  };
};

export const countingMap = (sample: any) => {
  return {
    analysis_type: sample.typeAnalysis,
    analyst: sample.counting_analyst,
    counting_procedure: sample.counting_procedure,
    equipo: !Array.isArray(sample?.counting_equipment)
      ? sample.counting_equipment
      : undefined,
    detector: sample.counting_detector,
    total_cpm_sample: sample.total_cpm_sample,
    counting_date: sample.counting_date,
    sqp_tsie_white: sample.sqp_tsie_white,
    sqp_tsie_sample: sample.sqp_tsie_sample,
    applied_criterion: sample.applied_criterion || 0,
    variation_sqp_tsie: sample.variation_sqp_tsie,
    counting_valid: sample.counting_valid || 0,
    sample_cpm: sample.sample_cpm,
    gross_white_cpm: sample.gross_white_cpm,
    total_sample_counting_time: sample.total_sample_counting_time,
    total_white_counting_time: sample.total_white_counting_time,
    cpm_sample_quimioluminiscencia: sample.cpm_sample_quimioluminiscencia,
    cmp_white_quimioluminiscencia: sample.cmp_white_quimioluminiscencia,
    total_cpm_white: sample.total_cpm_white,
    id: sample.countingId,
    preparation: sample.preparationId,
    processing: sample.processingId,
    state: 1,
    aa_measure: sample.measurementId,
    unit_types: sample.counting_unit_types,
    noise_back_1mc: sample.noise_back_1mc
      ? sample.noise_back_1mc.value
      : undefined,
    equipment: sample.counting_equipment,
    first_counting_date: sample.first_counting_date
      ? sample.first_counting_date
      : null,
    first_counting_time: sample.first_counting_time,
    first_counting_cpm: sample.first_counting_cpm,
    second_counting_date: sample.second_counting_date
      ? sample.second_counting_date
      : null,
    second_counting_time: sample.second_counting_time,
    second_counting_cpm: sample.second_counting_cpm,
    amount_sample: sample.amount_sample,
  };
};

export const countingMapBRBT = (sample: any) => {
  return {
    analysis_type: sample.typeAnalysis,
    id: sample.countingId,
    processing: sample.processingId,
    state: 1,
    analyst: sample.counting_analyst,
    counting_procedure: sample.counting_procedure,
    equipment: sample.counting_equipment,
    detector: sample.counting_detector,
    counting_date: sample.counting_date,
    total_sample_counting_time: sample.total_sample_counting_time,
    first_counting_cpm: sample.first_counting_cpm,
    second_counting_cpm: sample.second_counting_cpm,
    counting_valid: sample.counting_valid,
    preparation: sample.preparationId,
    aa_measure: sample.measurementId,
  };
};

export const countingATMap = (sample: any) => {
  return {
    analysis_type: sample.typeAnalysis,
    analyst: sample.counting_analyst,
    counting_procedure: sample.counting_procedure,
    equipment: sample.counting_equipment,
    detector: sample.counting_detector,
    total_cpm_sample: sample.total_cpm_sample,
    counting_date: sample.counting_date,
    sqp_tsie_white: sample.sqp_tsie_white,
    sqp_tsie_sample: sample.sqp_tsie_sample,
    variation_sqp_tsie: sample.variation_sqp_tsie,
    counting_valid: sample.counting_valid,
    sample_cpm: sample.sample_cpm,
    gross_white_cpm: sample.gross_white_cpm,
    total_sample_counting_time: sample.total_sample_counting_time,
    total_white_counting_time: sample.total_white_counting_time,
    cpm_sample_quimioluminiscencia: sample.cpm_sample_quimioluminiscencia,
    id: sample.countingId,
    preparation: sample.preparationId,
    processing: sample.processingId,
    state: 1,
    aa_measure: sample.measurementId,
    applied_criterion: 0,
  };
};

export const countingIodo131 = (sample: any) => {
  return {
    analysis_type: sample.typeAnalysis,
    analyst: sample.counting_analyst,
    counting_procedure: sample.counting_procedure,
    equipment: sample.counting_equipment,
    detector: sample.counting_detector,
    counting_date: sample.counting_date,
    total_sample_counting_time: sample.total_sample_counting_time,
    total_cpm_sample: sample.total_cpm_sample,
    counting_valid: 0,
    sample_cpm: sample.sample_cpm,
    id: sample.countingId,
    preparation: sample.preparationId,
    processing: sample.processingId,
    state: 1,
    applied_criterion: 0,
  };
};

export const countingGTIG = (sample: any) => {
  return {
    analysis_type: sample.typeAnalysis,
    analyst: sample.counting_analyst,
    counting_procedure: sample.counting_procedure,
    equipment: sample.counting_equipment,
    counting_date: sample.counting_date,
    counting_valid: sample.counting_valid_select
      ? sample.counting_valid_select.value
      : undefined,
    preparation: sample.preparationId,
    processing: sample.processingId,
    id: sample.countingId,
    state: 1,
    geometry_height: sample.geometry_height,
    first_counting_time: sample.first_counting_time,
  };
};

export const measurementMap = (sample: any) => {
  return {
    id: sample.measurementId,
    analysis_type: sample.typeAnalysis,
    preparation: sample.preparationId,
    analyst: sample.measurement_analyst,
    procedure: sample.measurement_processing_procedure,
    equipment: sample.measurement_equipment,
    start_date_time: sample.measurement_date_time,
    mass_glass_without_sample: sample.mass_glass_without_sample,
    mass_glass_with_sample: sample.mass_glass_with_sample,
    mass_sample: sample.mass_sample,
    aliquot_s: sample.aliquot_s,
    carrier_volume_added_sample: sample.carrier_volume_added_sample,
    carrier_concentration: sample.carrier_concentration,
    carrier_mass: sample.carrier_mass,
    dissolution_flask_volume: sample.dissolution_flask_volume,
    atomic_absorption_measurement: sample.atomic_absorption_measurement,
    counting_by_one: sample.counting_by_one,
    vial_sample_quantity: sample.vial_sample_quantity,
    valid_measure: sample.valid_measure,
    sr_aa_mg_l: sample.sr_aa_mg_l,
    sr_aa_mg: sample.sr_aa_mg,
    performance: sample.measurement_performance,
    sr_co3_aa: sample.sr_co3_aa,
    result_to_calculation: sample.result_to_calculation,
    state: 1,
  };
};
export const measurementMapBRBT = (sample: any) => {
  return {
    sample_registration: sample.sample_registration,
    id: sample.measurementId,
    analysis_type: sample.typeAnalysis,
    preparation: sample.preparationId,
    state: 1,
    analyst: sample.measurement_analyst,
    procedure: sample.measurement_procedure,
    start_date_time: sample.measurement_start_date_time,
    equipment: sample.measurement_equipment,
    dilution_factor: sample.measurement_dilution_factor,
    aa_equipment_result: sample.measurement_aa_equipment_result,
    result_to_calculation: sample.measurement_result_to_calculation,
    mg_potasio: sample.measurement_mg_potasio,
  };
};

export const calculationMap = (sample: any) => {
  return {
    analysis_type: sample.typeAnalysis,
    analyst: sample.calculation_analyst,
    equipment: sample.calculation_equipment,
    laboratory: sample.laboratory,
    id: sample.calculationId,
    start_date_time: sample.date_start_sampling || dayjs().format("YYYY-MM-DD"),
    end_date_time: sample.date_end_sampling,
    units: sample.calculation_units,
    tsie_sample: sample.calculation_tsie_sample || 0,
    tsie_white: sample.calculation_tsie_white || 0,
    shared: sample.calculation_shared === "No" ? 0 : 1,
    counting_date: sample.counting_date,
    sample_gross_cpm: sample.sample_gross_cpm || 0,
    gross_target_cpm: sample.gross_target_cpm || 0,
    total_time_counting: sample.total_time_counting,
    total_white_counting_time: sample.total_white_counting_time || 0,
    preparation: sample.preparationId,
    counting: sample.countingId,
    calculation: sample.calculation,
    sample_quantity: sample.calculation_amount_sample,
    counting_entity: sample.countingId,
    processing: sample.processingId,
    calculation_equipment: sample.calculation_equipment,
    calculation_procedure: sample.calculation_procedure,
    calculation_date: sample.calculation_date,
    state: 1,
    calculation_method: sample.calculation_method,
    detector: sample.calculation_detector,
    fondos: sample.calculation_fondo,
    fondo_alfa: sample.fondo_alfa,
  };
};

export const calculationIG = (sample: any) => {
  return {
    id: sample.calculationId,
    analyst: sample.calculation_analyst,
    analysis_type: sample.typeAnalysis,
    start_date_time: sample.date_start_sampling || dayjs().format("YYYY-MM-DD"),
    end_date_time: sample.date_end_sampling,
    sample_quantity: sample.calculation_amount_sample,
    units: sample.calculation_units,
    calculation_equipment: sample.calculation_equipment,
    equipment: sample.calculation_equipment,
    tsie_sample: sample.calculation_tsie_sample || 0,
    shared: sample.calculation_shared,
    laboratory: sample.laboratory,
    calculation_procedure: sample.calculation_procedure,
    self_absorption_factor: sample.calculation_self_absorption_factor,
    geometric_factor: sample.calculation_geometric_factor,
    calculation_date: sample.calculation_date,
    report_characterization: sample.calculation_type_report.includes(
      "report_characterization"
    ),
    only_lid: sample.calculation_type_report.includes("only_lid"),
    dataIsotopes: sample.dataIsotopes,
    source_app_calculation_info:
      sample.source_app_calculation_info ||
      sample.source_app_calculation_info_previus,
    source_app_calculation_info_file:
      sample.source_app_calculation_info_file_genie
        ? sample.source_app_calculation_info_file_genie
        : sample.source_app_calculation_info_file_exe
        ? sample.source_app_calculation_info_file_exe
        : undefined,
    source_app_calculation_info_file_2:
      sample.source_app_calculation_info_file_2_exe
        ? sample.source_app_calculation_info_file_2_exe
        : undefined,
    counting_entity: sample.countingId,
    processing: sample.processingId,
    state: 1,
    preparation: sample.preparationId,
    calculation: sample.calculation,
    pico_counts_341: sample.pico_counts_341,
    pico_counts_911: sample.pico_counts_911,
    calculation_date_countig_file: sample.calculation_date_countig_file,
    calculation_equipment_file: sample.calculation_equipment_file,
    calculation_time_count: sample.calculation_time_count,
    calculation_detector: sample.calculation_detector,
  };
};

export const calculationGT = (sample: any) => {
  return {
    id: sample.calculationId,
    analyst: sample.calculation_analyst,
    analysis_type: sample.typeAnalysis,
    start_date_time: sample.date_start_sampling || dayjs().format("YYYY-MM-DD"),
    end_date_time: sample.date_end_sampling,
    sample_quantity: sample.calculation_amount_sample,
    units: sample.calculation_units,
    calculation_equipment: sample.counting_equipment,
    equipment: sample.counting_equipment,
    tsie_sample: sample.calculation_tsie_sample || 0,
    shared: sample.calculation_shared,
    laboratory: sample.laboratory,
    calculation_procedure: sample.calculation_procedure,
    self_absorption_factor: sample.calculation_self_absorption_factor,
    geometric_factor: sample.calculation_geometric_factor,
    calculation_date: sample.calculation_date,
    report_characterization: sample.calculation_type_report.includes(
      "report_characterization"
    ),
    only_lid: sample.calculation_type_report.includes("only_lid"),
    dataIsotopes: sample.dataIsotopes,
    source_app_calculation_info:
      sample.source_app_calculation_info ||
      sample.source_app_calculation_info_previus,
    source_app_calculation_info_file:
      sample.source_app_calculation_info_file_genie
        ? sample.source_app_calculation_info_file_genie
        : sample.source_app_calculation_info_file_exe
        ? sample.source_app_calculation_info_file_exe
        : undefined,
    source_app_calculation_info_file_2:
      sample.source_app_calculation_info_file_2_exe
        ? sample.source_app_calculation_info_file_2_exe
        : undefined,
    counting_entity: sample.countingId,
    processing: sample.processingId,
    state: 1,
    preparation: sample.preparationId,
    calculation: sample.calculation,
    pico_counts_341: sample.pico_counts_341,
    pico_counts_911: sample.pico_counts_911,
    calculation_date_countig_file: sample.calculation_date_countig_file,
    calculation_equipment_file: sample.calculation_equipment_file,
    performance: sample.calculation_performance,
    cps_spectrum_area_shows: sample.cps_spectrum_area_shows,
    integral_spectrum_sample_accounts: sample.integral_spectrum_sample_accounts,
    total_sample_counting_time: sample.total_sample_counting_time,
  };
};

export const calculationMapSR = (sample: any) => {
  return {
    analysis_type: sample.typeAnalysis,
    analyst: sample.calculation_analyst,
    equipment: sample.calculation_equipment,
    laboratory: sample.laboratory,
    id: sample.calculationId,
    start_date_time: sample.date_start_sampling || dayjs().format("YYYY-MM-DD"),
    end_date_time: sample.date_end_sampling,
    units: sample.calculation_units,
    tsie_sample: sample.calculation_tsie_sample || 0,
    tsie_white: sample.calculation_tsie_white || 0,
    shared: sample.calculation_shared,
    counting_date: sample.counting_date,
    sample_gross_cpm: sample.sample_gross_cpm || 0,
    gross_target_cpm: sample.gross_target_cpm || 0,
    total_time_counting: sample.total_time_counting,
    total_white_counting_time: sample.total_white_counting_time || 0,
    preparation: sample.preparationId,
    counting: sample.countingId,
    calculation: sample.calculation,
    sample_quantity: sample.calculation_amount_sample,
    counting_entity: sample.countingId,
    processing: sample.processingId,
    calculation_equipment: sample.calculation_equipment,
    calculation_procedure: sample.calculation_procedure,
    calculation_date: sample.calculation_date,
    state: 1,
    calculation_method: sample.calculation_method,
    detector: sample.calculation_detector,
    fondos: sample.calculation_fondo,
    calculation_precipitation_first_counting:
      sample.calculation_precipitation_first_counting,
    calculation_precipitation_second_counting:
      sample.calculation_precipitation_second_counting,
    calculation_precipitation_final_time:
      sample.calculation_precipitation_final_time,
    calculation_sampling_time_min: sample.calculation_sampling_time_min,
    calculation_second_counting_cpm: sample.calculation_second_counting_cpm,
    calculation_second_counting_time: sample.calculation_second_counting_time,
    calculation_second_counting_date: sample.calculation_second_counting_date,
    calculation_first_counting_cpm: sample.calculation_first_counting_cpm,
    calculation_first_counting_time: sample.calculation_first_counting_time,
    calculation_first_counting_date: sample.calculation_first_counting_date,
    calculation_dry_wet_weight: sample.calculation_dry_wet_weight,
    performance: sample.calculation_performance,
  };
};
export const calculationMapBRBT = (sample: any) => {
  return {
    analysis_type: sample.typeAnalysis,
    analyst: sample.calculation_analyst,
    equipment: sample.calculation_equipment,
    laboratory: sample.laboratory,
    id: sample.calculationId,
    start_date_time: sample.date_start_sampling || dayjs().format("YYYY-MM-DD"),
    end_date_time: sample.date_end_sampling,
    processing: sample.processingId,
    state: 1,
    counting: sample.countingId,
    counting_entity: sample.countingId,
    preparation: sample.preparationId,
    counting_date: sample.calculation_counting_date,
    //fondos: sample.fondos,
    fondos: sample.calculation_fondo,
    fondo_alfa: sample.calculation_fondo_alfa,
    sample_quantity: sample.calculation_amount_sample,
    units: sample.calculation_units,
    calculation_precipitation: sample.planchette_with_precipitate,
    mg_potasio: sample.calculation_mg_potasio,
    calculation_dry_wet_weight: sample.calculation_dry_wet_weight,
    calculation_procedure: sample.calculation_procedure,
    detector: sample.calculation_detector,
    calculation_first_counting_date: sample.calculation_first_counting_date,
    total_time_counting: sample.total_time_counting,
    calculation: sample.calculation || "",
    calculation_date: sample.calculation_date,
    calculation_method: sample.calculation_method,
  };
};

export const calculationMapDP = (sample: any) => {
  return {
    analysis_type: sample.typeAnalysis,
    analyst: sample.calculation_analyst,
    equipment: sample.calculation_equipment,
    laboratory: sample.laboratory,
    id: sample.calculationId,
    start_date_time: sample.date_start_sampling || dayjs().format("YYYY-MM-DD"),
    end_date_time: sample.date_end_sampling,
    units: sample.calculation_units,
    tsie_sample: sample.calculation_tsie_sample || 0,
    tsie_white: sample.calculation_tsie_white || 0,
    shared: sample.calculation_shared,
    counting_date: sample.counting_date,
    sample_gross_cpm: sample.sample_gross_cpm || 0,
    gross_target_cpm: sample.gross_target_cpm || 0,
    total_time_counting: sample.total_time_counting,
    total_white_counting_time: sample.total_white_counting_time || 0,
    preparation: sample.preparationId,
    counting: sample.countingId,
    calculation: sample.calculation || "",
    sample_quantity: sample.calculation_amount_sample,
    counting_entity: sample.countingId,
    processing: sample.processingId,
    calculation_equipment: sample.calculation_equipment,
    calculation_procedure: sample.calculation_procedure,
    calculation_date: sample.calculation_date,
    state: 1,
    calculation_method: sample.calculation_method,
    detector: sample.calculation_detector,
    exposition_days: sample.exposition_days,
    transit_days: sample.transit_days,
    error_lecturas_usv: sample.error_lecturas_usv,
    valor_media_lecturas_usv: sample.valor_media_lecturas_usv,
    lectura_4_usv: sample.lectura_4_usv,
    lectura_3_usv: sample.lectura_3_usv,
    lectura_2_usv: sample.lectura_2_usv,
    lectura_1_usv: sample.lectura_1_usv,
    dosimetro: sample.dosimetro,
    group: sample.group?.value,
    is_read: sample.is_read?.value,
    is_delete: sample.is_delete?.value,
  };
};

export const calculationMapPuAmCM = (sample: any) => {
  return {
    analysis_type: sample.typeAnalysis,
    analyst: sample.calculation_analyst,
    equipment: sample.calculation_equipment,
    laboratory: sample.laboratory,
    id: sample.calculationId,
    start_date_time: sample.date_start_sampling || dayjs().format("YYYY-MM-DD"),
    end_date_time: sample.date_end_sampling,
    units: sample.calculation_units,
    tsie_sample: sample.calculation_tsie_sample || 0,
    tsie_white: sample.calculation_tsie_white || 0,
    shared: sample.calculation_shared,
    counting_date: sample.counting_date,
    sample_gross_cpm: sample.sample_gross_cpm || 0,
    gross_target_cpm: sample.gross_target_cpm || 0,
    total_time_counting: sample.total_time_counting,
    total_white_counting_time: sample.total_white_counting_time || 0,
    preparation: sample.preparationId,
    counting: sample.countingId,
    calculation: sample.calculation,
    sample_quantity: sample.calculation_amount_sample,
    counting_entity: sample.countingId,
    processing: sample.processingId,
    calculation_equipment: sample.calculation_equipment,
    calculation_procedure: sample.calculation_procedure,
    calculation_date: sample.calculation_date,
    state: 1,
    calculation_method: sample.calculation_method,
    detector: sample.calculation_detector,
    fondos: sample.calculation_fondo,
    fondo_alfa: sample.fondo_alfa,
    calculation_date_countig_file: sample.calculation_date_countig_file,
    calculation_time_count: sample.calculation_time_count,
    source_app_calculation_info: sample.source_app_calculation_info,
    calculation_equipment_file: sample.calculation_equipment_file,
    calculation_detectors: sample.calculation_detectors,
    source_app_calculation_info_file:
      sample.source_app_calculation_info_file_genie
        ? sample.source_app_calculation_info_file_genie
        : undefined,
    trazador: sample.trazador,
  };
};

export const removePreparationByIndex = (preparations: any[], idx: number) =>
  preparations.filter((_, index) => index !== idx);

export const removeMeasurementByIndex = (
  preparations: any[],
  idxPreparation: number,
  idxMeasurement: number
) =>
  preparations.map((preparation, prepIndex) => {
    if (prepIndex === idxPreparation) {
      const updatedMeasure = preparation.aa_measures?.filter(
        (_: any, measureIndex: number) => measureIndex !== idxMeasurement
      );
      return { ...preparation, aa_measures: updatedMeasure };
    }
    return preparation;
  });

export const removeCountingByIndexWithMeasurement = (
  preparations: any[],
  idxPreparation: number,
  idxMeasurement: number,
  idxCounting: number
) =>
  preparations.map((preparation, prepIndex) => {
    if (prepIndex === idxPreparation) {
      const updatedMeasure = preparation.aa_measures?.map(
        (measures: any, measuresIndex: number) => {
          if (measuresIndex === idxMeasurement) {
            const updatedCalculations = measures.countings?.filter(
              (_: any, calcIndex: number) => calcIndex !== idxCounting
            );
            return { ...measures, countings: updatedCalculations };
          }
          return measures;
        }
      );
      return { ...preparation, aa_measures: updatedMeasure };
    }
    return preparation;
  });

export const removeCountingByIndex = (
  preparations: any[],
  idxPreparation: number,
  idxCounting: number
) =>
  preparations.map((preparation, prepIndex) => {
    if (prepIndex === idxPreparation) {
      const updatedCountings = preparation.countings?.filter(
        (_: any, countIndex: number) => countIndex !== idxCounting
      );
      return { ...preparation, countings: updatedCountings };
    }
    return preparation;
  });

export const removeCalculationByIndex = (
  preparations: any[],
  idxPreparation: number,
  idxCounting: number,
  idxCalculation: number
) =>
  preparations.map((preparation, prepIndex) => {
    if (prepIndex === idxPreparation) {
      const updatedCountings = preparation.countings?.map(
        (counting: any, countIndex: number) => {
          if (countIndex === idxCounting) {
            const updatedCalculations = counting.calculations?.filter(
              (_: any, calcIndex: number) => calcIndex !== idxCalculation
            );
            return { ...counting, calculations: updatedCalculations };
          }
          return counting;
        }
      );
      return { ...preparation, countings: updatedCountings };
    }
    return preparation;
  });

export const removeCalculationByIndexWithMeasurement = (
  preparations: any[],
  idxPreparation: number,
  idxMeasurement: number,
  idxCounting: number,
  idxCalculation: number
) =>
  preparations.map((preparation, prepIndex) => {
    if (prepIndex === idxPreparation) {
      const updatedMeasures = preparation.aa_measures?.map(
        (measure: any, measureIndex: number) => {
          if (measureIndex === idxMeasurement) {
            const updatedCountings = measure.countings?.map(
              (counting: any, countingIndex: number) => {
                if (countingIndex === idxCounting) {
                  const updatedCalculations = counting.calculations?.filter(
                    (_: any, calcIndex: number) => calcIndex !== idxCalculation
                  );
                  return { ...counting, calculations: updatedCalculations };
                }
                return counting;
              }
            );
            return { ...measure, countings: updatedCountings };
          }
          return measure;
        }
      );
      return { ...preparation, aa_measures: updatedMeasures };
    }
    return preparation;
  });

export const updatePreparations = (
  preparationsList: any[],
  updatedPreparations: any[],
  idTypeAnalysis: number
) => [
  ...preparationsList.filter(
    (preparation) =>
      !preparation.analysis_type.some(
        (analysis) => analysis.id === idTypeAnalysis
      )
  ),
  ...updatedPreparations,
];

export const validateFields = (
  data: IFormConditioning,
  setTabValueTritioVapor: (value: number) => void
) => {
  const errors: string[] = [];

  if (
    !data["analysis_equipment"] ||
    data["analysis_equipment"].length === 0 ||
    !data["sampleCountVapor"]
  ) {
    errors.push(
      "El la pestaña 'Datos Volumen' tienes campos es requerido sin ingresar"
    );
    setTabValueTritioVapor(0);
  } else if (
    !data["numberOfTraps"] ||
    !data["rowsSilicaGelData"] ||
    data["rowsSilicaGelData"].length === 0 ||
    !data["waterRetainedValues"] ||
    data["waterRetainedValues"].length === 0
  ) {
    errors.push(
      "El la pestaña 'Datos Silicagel' tienes campos es requerido sin ingresar."
    );
    setTabValueTritioVapor(1);
  } else if (
    !data["start_date"] ||
    !data["analysis_equipment"] ||
    data["analysis_equipment"].length === 0 ||
    !data["analyzedWaterVolume"] ||
    !data["analysis_unit_types"] ||
    !data["analysis_analyst"] ||
    !data["scintillator_lot_number"]
  ) {
    errors.push(
      "El la pestaña 'Seguimiento' tienes campos es requerido sin ingresar."
    );
    setTabValueTritioVapor(2);
  }

  return errors;
};

export const preloadProcessings = (processings: any, user?: UserType) => {
  let returnProcessings: {
    processing_analyst_grinding?: UserType;
    processing_procedure_grinding?: string;
    processing_equipment_grinding?: string;
    processing_date_time_grinding?: string;
    sample_quantity_grinding?: number;
    processing_analyst_sifting?: UserType;
    processing_procedure_sifting?: string;
    analytical_balance_sifting?: string;
    processing_date_time_sifting?: string;
    sample_quantity_sifting?: number;
    wet_weight_sifting?: number;
    gross_weight_sifting?: number;
    processing_analyst_evaporation?: UserType;
    processing_procedure_evaporation?: string;
    processing_equipment_evaporation?: string;
    processing_reagent_evaporation?: string;
    processing_date_time_evaporation?: string;
    processing_date_time_end_evaporation?: string;
    sample_volume_evaporation?: number;
    processing_analyst_digestion?: UserType;
    processing_procedure_digestion?: string;
    processing_equipment_digestion?: string;
    processing_date_time_digestion?: string;
    sample_quantity_digestion?: number;
    processing_analyst_drying?: UserType;
    processing_procedure_drying?: string;
    processing_equipment_drying?: string;
    processing_date_time_drying?: string;
    processing_date_time_end_drying?: string;
    capsules_number_drying?: number;
    processing_analyst_calcination?: UserType;
    processing_procedure_calcination?: string;
    processing_equipment_calcination?: string;
    processing_date_time_calcination?: string;
    processing_date_time_end_calcination?: string;
    capsules_number_calcination?: number;
    rowsCurvaDrying?: any;
    rowsCurvaDryingCalcination?: any;
    rowsCurvaCalcination?: any;
    rowsCurvaCalcinationDrying?: any;
    processing_date_time_not_applicable?: string;
    processing_analyst_not_applicable?: UserType;
    processing_analyst_chipping?: UserType;
    processing_procedure_chipping?: string;
    processing_equipment_chipping?: string;
    processing_date_time_chipping?: string;
    sample_quantity_chipping?: number;
    idProcessing_chipping?: number;
    idProcessing_grinding?: number;
    capsules_number_old?: number;
    wet_dry?: number;
    dry_calcination?: number;
  } = {
    processing_analyst_grinding: user,
    processing_analyst_sifting: user,
    processing_analyst_evaporation: user,
    processing_analyst_digestion: user,
    processing_analyst_drying: user,
    processing_analyst_calcination: user,
    processing_analyst_chipping: user,
  };
  if (!processings || processings.length === 0) return returnProcessings;

  processings.forEach((processing) => {
    const procesingCategory =
      processing.processing_category && processing.processing_category?.id
        ? `${processing.processing_category.id}`
        : "";

    switch (procesingCategory) {
      case mapsProcessingCategory["grinding"]:
        returnProcessings = {
          ...returnProcessings,
          processing_analyst_grinding: processing.analyst || user,
          processing_procedure_grinding: processing.processing_procedure,
          processing_equipment_grinding: processing.equipment,
          processing_date_time_grinding: formatDateOrNow(processing.date_time),
          sample_quantity_grinding: processing.sample_quantity,
          idProcessing_grinding: processing.id,
        };
        break;
      case mapsProcessingCategory["sifting"]:
        returnProcessings = {
          ...returnProcessings,
          processing_analyst_sifting: processing.analyst || user,
          processing_procedure_sifting: processing.processing_procedure,
          analytical_balance_sifting: processing.equipment,
          processing_date_time_sifting: formatDateOrNow(processing.date_time),
          sample_quantity_sifting: processing.sample_quantity,
          wet_weight_sifting: processing.wet_weight,
          gross_weight_sifting: processing.gross_weight,
        };
        break;
      case mapsProcessingCategory["evaporation"]:
        returnProcessings = {
          ...returnProcessings,
          processing_analyst_evaporation: processing.analyst || user,
          processing_procedure_evaporation: processing.processing_procedure,
          processing_equipment_evaporation: processing.equipment,
          processing_reagent_evaporation: processing.reagent,
          processing_date_time_evaporation: formatDateOrNow(
            processing.date_time
          ),
          processing_date_time_end_evaporation: processing.date_time_end,
          sample_volume_evaporation: processing.sample_volume,
        };
        break;
      case mapsProcessingCategory["digestion"]:
        returnProcessings = {
          ...returnProcessings,
          processing_analyst_digestion: processing.analyst || user,
          processing_procedure_digestion: processing.processing_procedure,
          processing_equipment_digestion: processing.equipment,
          processing_date_time_digestion: formatDateOrNow(processing.date_time),
          sample_quantity_digestion: processing.sample_quantity,
        };
        break;
      case mapsProcessingCategory["drying"]:
        const { type1, type2, numberCapsules } = processCapsuleWeights(
          processing.processing_capsule_weights
        );
        returnProcessings = {
          ...returnProcessings,
          processing_analyst_drying: processing.analyst || user,
          processing_procedure_drying: processing.processing_procedure,
          processing_equipment_drying: processing.equipment,
          processing_date_time_drying: formatDateOrNow(processing.date_time),
          processing_date_time_end_drying: processing.date_time_end,
          rowsCurvaDryingCalcination: type1,
          rowsCurvaDrying: type2,
          capsules_number_drying: numberCapsules,
          capsules_number_old: numberCapsules,
          wet_dry: processing.wet_dry,
        };
        break;
      case mapsProcessingCategory["calcination"]:
        const { typeC1, typeC2, numberCapsulesCal } =
          processCapsuleWeightsCalcination(
            processing.processing_capsule_weights
          );

        returnProcessings = {
          ...returnProcessings,
          processing_analyst_calcination: processing.analyst || user,
          processing_procedure_calcination: processing.processing_procedure,
          processing_equipment_calcination: processing.equipment,
          processing_date_time_calcination: formatDateOrNow(
            processing.date_time
          ),
          processing_date_time_end_calcination: formatDateOrNow(
            processing.date_time_end
          ),
          rowsCurvaCalcination: typeC1,
          rowsCurvaCalcinationDrying: typeC2,
          capsules_number_calcination: numberCapsulesCal,
          capsules_number_old: numberCapsulesCal,
          dry_calcination: processing.dry_calcination,
        };
        break;
      case mapsProcessingCategory["chipping"]:
        returnProcessings = {
          ...returnProcessings,
          processing_analyst_chipping: processing.analyst || user,
          processing_procedure_chipping: processing.processing_procedure,
          processing_equipment_chipping: processing.equipment,
          processing_date_time_chipping: formatDateOrNow(processing.date_time),
          sample_quantity_chipping: processing.sample_quantity,
          idProcessing_chipping: processing.id,
        };
        break;
      case mapsProcessingCategory["not_applicable"]:
        returnProcessings = {
          processing_date_time_not_applicable: formatDateOrNow(
            processing.date_time
          ),
          processing_analyst_not_applicable: processing.analyst || user,
        };
        break;
      default:
        break;
    }
  });

  return returnProcessings;
};

export const convertProcessing = (form: any) => {
  const {
    rowsCurvaCalcination,
    rowsCurvaDryingCalcination,
    rowsCurvaDrying,
    rowsCurvaCalcinationDrying,
  } = form;

  const data = {
    rowsCurvaCalcination,
    rowsCurvaDryingCalcination,
    rowsCurvaDrying,
    rowsCurvaCalcinationDrying,
  };

  // Resultado final separado por tipo de procesamiento
  const result: { drying: any[]; calcinacion: any[] } = {
    drying: [],
    calcinacion: [],
  };

  const getCapsuleKeys = (row, prefix) =>
    row
      ? Object.keys(row).filter((key) => key.startsWith(prefix) && row[key])
      : [];

  // Procesar "rowsCurvaDrying" para id "dry_secado_1"
  if (data.rowsCurvaDrying) {
    const drySecado1 = data.rowsCurvaDrying.find(
      (row) => row.id === "dry_secado_1"
    );
    if (drySecado1) {
      const dryCapsules = getCapsuleKeys(drySecado1, "capsule_dry");
      dryCapsules.forEach((key) => {
        result.drying.push({
          weight_with_capsule: drySecado1[key],
          type: 2,
        });
      });
    }
  }

  // Procesar "rowsCurvaCalcinationDrying" para id "secado_1"
  if (data.rowsCurvaCalcinationDrying) {
    const secado1 = data.rowsCurvaCalcinationDrying.find(
      (row) => row.id === "secado_1"
    );
    if (secado1) {
      const secadoCapsules = getCapsuleKeys(secado1, "capsule");
      secadoCapsules.forEach((key) => {
        result.calcinacion.push({
          weight_with_capsule: secado1[key],
          type: 2,
        });
      });
    }
  }

  // Procesar "rowsCurvaCalcination" para "calcinacion_1" y "calcinacion_2"
  if (data.rowsCurvaCalcination) {
    const calcinacion1 = data.rowsCurvaCalcination.find(
      (row) => row.id === "calcinacion_1"
    );
    const calcinacion2 = data.rowsCurvaCalcination.find(
      (row) => row.id === "calcinacion_2"
    );

    if (calcinacion1 && calcinacion2) {
      const capsuleKeys1 = getCapsuleKeys(calcinacion1, "capsule");
      const capsuleKeys2 = getCapsuleKeys(calcinacion2, "capsule");

      for (let i = 0; i < capsuleKeys1.length; i++) {
        if (calcinacion1[capsuleKeys1[i]] && calcinacion2[capsuleKeys2[i]]) {
          result.calcinacion.push({
            weight_with_capsule: calcinacion2[capsuleKeys2[i]],
            capsule_weight: calcinacion1[capsuleKeys1[i]],
            type: 1,
          });
        }
      }
    }
  }

  // Procesar "rowsCurvaDryingCalcination" para "drying_1" y "drying_2"
  if (data.rowsCurvaDryingCalcination) {
    const drying1 = data.rowsCurvaDryingCalcination.find(
      (row) => row.id === "drying_1"
    );
    const drying2 = data.rowsCurvaDryingCalcination.find(
      (row) => row.id === "drying_2"
    );

    if (drying1 && drying2) {
      const capsuleKeys1 = getCapsuleKeys(drying1, "capsule_dry");
      const capsuleKeys2 = getCapsuleKeys(drying2, "capsule_dry");

      for (let i = 0; i < capsuleKeys1.length; i++) {
        if (drying1[capsuleKeys1[i]] && drying2[capsuleKeys2[i]]) {
          result.drying.push({
            weight_with_capsule: drying2[capsuleKeys2[i]],
            capsule_weight: drying1[capsuleKeys1[i]],
            type: 1,
          });
        }
      }
    }
  }

  return result;
};

export const mapProcessingCategoryToDefaults = (
  processingCategoryId,
  processing
) => {
  switch (processingCategoryId) {
    case 1: // Molienda
      return {
        processing_analyst_grinding: processing?.analyst || undefined,
        processing_procedure_grinding:
          processing?.processing_procedure || undefined,
        processing_equipment_grinding: processing?.equipment || undefined,
        processing_date_time_grinding: formatDateOrNow(processing?.date_time),
        sample_quantity_grinding: processing?.sample_quantity || undefined,
      };
    case 2: // Tamizado
      return {
        processing_analyst_sifting: processing?.analyst || undefined,
        processing_procedure_sifting:
          processing?.processing_procedure || undefined,
        analytical_balance_sifting: processing?.analytical_balance || undefined,
        processing_date_time_sifting: formatDateOrNow(processing?.date_time),
        gross_weight_sifting: processing?.gross_weight || undefined,
        wet_weight_sifting: processing?.wet_weight || undefined,
      };
    case 3: // Secado
      return {
        processing_analyst_drying: processing?.analyst || undefined,
        processing_procedure_drying:
          processing?.processing_procedure || undefined,
        processing_equipment_drying: processing?.equipment || undefined,
        processing_date_time_drying: formatDateOrNow(processing?.date_time),
        wet_weight_drying: processing?.wet_weight || undefined,
      };
    case 4: // Calcinación
      return {
        processing_analyst_calcination: processing?.analyst || undefined,
        processing_procedure_calcination:
          processing?.processing_procedure || undefined,
        processing_equipment_calcination: processing?.equipment || undefined,
        processing_date_time_calcination: formatDateOrNow(
          processing?.date_time
        ),
        sample_quantity_calcination: processing?.sample_quantity || undefined,
      };
    case 5: // Evaporación
      return {
        processing_analyst_evaporation: processing?.analyst || undefined,
        processing_procedure_evaporation:
          processing?.processing_procedure || undefined,
        processing_equipment_evaporation: processing?.equipment || undefined,
        processing_date_time_evaporation: formatDateOrNow(
          processing?.date_time
        ),
        sample_volume_evaporation: processing?.sample_volume || undefined,
      };
    case 6: // Digestión
      return {
        processing_analyst_digestion: processing?.analyst || undefined,
        processing_procedure_digestion:
          processing?.processing_procedure || undefined,
        processing_equipment_digestion: processing?.equipment || undefined,
        processing_date_time_digestion: formatDateOrNow(
          processing?.date_time
        ),
        sample_quantity_digestion: processing?.sample_quantity || undefined,
      };
    case 8: // Virutado
      return {
        processing_analyst_chipping: processing?.analyst || undefined,
        processing_procedure_chipping:
          processing?.processing_procedure || undefined,
        processing_equipment_chipping: processing?.equipment || undefined,
        processing_date_time_chipping: formatDateOrNow(
          processing?.date_time
        ),
        sample_quantity_chipping: processing?.sample_quantity || undefined,
      };
    default:
      return {};
  }
};

export function processCapsuleWeights(weights) {
  // Filtrar por tipo
  const type1 = weights.filter((item) => item.type === 1);
  const type2 = weights.filter((item) => item.type === 2);

  // Procesar tipo 1
  const dryingType1 = {
    id: "drying_1",
    label: "Tara cápsula (g)",
    ...Object.fromEntries(
      type1.map((item, index) => [
        `capsule_dry_${index + 1}`,
        item.capsule_weight,
      ])
    ),
    total: type1.reduce(
      (sum, item) => sum + Number(item.capsule_weight || 0),
      0
    ),
  };

  const dryingType1_2 = {
    id: "drying_2",
    label: "Peso húmedo muestra + cápsula (g)",
    ...Object.fromEntries(
      type1.map((item, index) => [
        `capsule_dry_${index + 1}`,
        item.weight_with_capsule,
      ])
    ),
    total: type1.reduce(
      (sum, item) => sum + Number(item.weight_with_capsule || 0),
      0
    ),
  };

  const dryingType1_3 = {
    id: "drying_3",
    label: "Peso húmedo muestra (g)",
    ...Object.fromEntries(
      type1.map((item, index) => [
        `capsule_dry_${index + 1}`,
        (
          Number(item.weight_with_capsule) - Number(item.capsule_weight)
        ).toFixed(2),
      ])
    ),
    total: type1
      .reduce(
        (sum, item) =>
          sum +
          (Number(item.weight_with_capsule) - Number(item.capsule_weight || 0)),
        0
      )
      .toFixed(2),
  };

  // Procesar tipo 2
  const dryingType2 = {
    id: "dry_secado_1",
    label: "Peso secado muestra + cápsula (g)",
    ...Object.fromEntries(
      type2.map((item, index) => [
        `capsule_dry_${index + 1}`,
        item.weight_with_capsule,
      ])
    ),
    total: type2.reduce(
      (sum, item) => sum + Number(item.weight_with_capsule || 0),
      0
    ),
  };

  const dryingType2_2 = {
    id: "dry_secado_2",
    label: "Peso secado muestra (g)",
    ...Object.fromEntries(
      type2.map((item, index) => [
        `capsule_dry_${index + 1}`,
        (
          Number(item.weight_with_capsule) -
          Number(type1[index]?.capsule_weight || 0)
        ).toFixed(2),
      ])
    ),
    total: type2
      .reduce(
        (sum, item, index) =>
          sum +
          (Number(item.weight_with_capsule || 0) -
            Number(type1[index]?.capsule_weight || 0)),
        0
      )
      .toFixed(2),
  };

  return {
    type1: [dryingType1, dryingType1_2, dryingType1_3],
    type2: [dryingType2, dryingType2_2],
    numberCapsules: Math.max(type1.length, type2.length),
  };
}

export function processCapsuleWeightsCalcination(weights) {
  // Filtrar por tipo
  const type1 = weights.filter((item) => item.type === 1);
  const type2 = weights.filter((item) => item.type === 2);

  // Procesar tipo 1
  const dryingType1 = {
    id: "calcinacion_1",
    label: LABELS_TABLE_CALCINATION.TARA,
    ...Object.fromEntries(
      type1.map((item, index) => [`capsule_${index + 1}`, item.capsule_weight])
    ),
    total: type1.reduce(
      (sum, item) => sum + Number(item.capsule_weight || 0),
      0
    ),
  };

  const dryingType1_2 = {
    id: "calcinacion_2",
    label: LABELS_TABLE_CALCINATION.PESO_CALCINADO,
    ...Object.fromEntries(
      type1.map((item, index) => [
        `capsule_${index + 1}`,
        item.weight_with_capsule,
      ])
    ),
    total: type1.reduce(
      (sum, item) => sum + Number(item.weight_with_capsule || 0),
      0
    ),
  };

  const dryingType1_3 = {
    id: "calcinacion_3",
    label: LABELS_TABLE_CALCINATION.PESO_CALCINADO_MUESTRA,
    ...Object.fromEntries(
      type1.map((item, index) => [
        `capsule_${index + 1}`,
        (
          Number(item.weight_with_capsule) - Number(item.capsule_weight)
        ).toFixed(2),
      ])
    ),
    total: type1
      .reduce(
        (sum, item) =>
          sum +
          (Number(item.weight_with_capsule) - Number(item.capsule_weight || 0)),
        0
      )
      .toFixed(2),
  };

  // Procesar tipo 2
  const dryingType2 = {
    id: "secado_1",
    label: LABELS_TABLE_DRYING.PESO_SECADO_MUESTRA_CAPSULA,
    ...Object.fromEntries(
      type2.map((item, index) => [
        `capsule_${index + 1}`,
        item.weight_with_capsule,
      ])
    ),
    total: type2.reduce(
      (sum, item) => sum + Number(item.weight_with_capsule || 0),
      0
    ),
  };

  const dryingType2_2 = {
    id: "secado_2",
    label: LABELS_TABLE_DRYING.PESO_SECADO_MUESTRA,
    ...Object.fromEntries(
      type2.map((item, index) => [
        `capsule_${index + 1}`,
        (
          Number(item.weight_with_capsule) -
          Number(type1[index]?.capsule_weight || 0)
        ).toFixed(2),
      ])
    ),
    total: type2
      .reduce(
        (sum, item, index) =>
          sum +
          (Number(item.weight_with_capsule || 0) -
            Number(type1[index]?.capsule_weight || 0)),
        0
      )
      .toFixed(2),
  };

  return {
    typeC1: [dryingType1, dryingType1_2, dryingType1_3],
    typeC2: [dryingType2, dryingType2_2],
    numberCapsulesCal: Math.max(type1.length, type2.length),
  };
}

export function syncData(preload, newData, capsuleNumber) {
  const result: any = [];

  // Agrupar la precarga por tipo
  const preloadByType = preload.reduce((acc, item) => {
    if (!acc[item.type]) acc[item.type] = [];
    acc[item.type].push(item);
    return acc;
  }, {});

  /*  newData = newData.map((item) => {
    if (item.type === 2 && item.capsule_weight === undefined) {
      return { ...item, capsule_weight: 0 };
    }
    return item;
  }); */

  newData.forEach((item) => {
    if (result.filter((i) => i.type === 1).length < capsuleNumber) {
      const matchingPreload = preloadByType[item.type] || [];
      if (matchingPreload.length > 0) {
        const { id } = matchingPreload.shift();
        result.push({ ...item, id }); // Asignar el ID
      } else {
        result.push({ ...item, id: undefined });
      }
    }
  });

  const copyResult = [...preload];
  const type2Items = copyResult.filter((item) => item.type === 2);
  const type1Items = copyResult.filter((item) => item.type === 1);
  const removedIds = [
    ...type2Items.slice(capsuleNumber).map((item) => item.id),
    ...type1Items.slice(capsuleNumber).map((item) => item.id),
  ];

  return { updatedData: result, idsToDelete: removedIds };
}

export function filterAndSortData<T>(
  data: Record<string, T>,
  isCurrier: boolean,
  analisisTypeCode: string
): {
  arrrayKeys: [string, T][];
  extendedLabelString: any;
} {
  // Elementos para sobrescribir en labelString según el tipo de análisis
  const overrideLabels: Record<string, Partial<typeof labelString>> = {
    [KeysTabsAnalysis.AT_E]: {
      primary_pattern_activity: {
        label: "Actividad del patrón primario: A0(Bq/g)",
        decimals: 3,
        type: LabelTypesOptions.NUMERICO,
        order: 4,
        id: "primary_pattern_activity-id",
      },
      primary_pattern_uncertainty: {
        label: "Incertidumbre del patrón primario (Bq/g): u(A0)",
        decimals: 3,
        type: LabelTypesOptions.NUMERICO,
        order: 5,
        id: "primary_pattern_uncertainty-id",
      },
      cpm_of_the_fc_with_largest_amount_of_scintillator: {
        label:
          "cpm de la fuente de calibración a espesor cero: U2cpmfc e=0 (K=1)",
        decimals: 2,
        type: LabelTypesOptions.NUMERICO,
        order: 10,
        id: "cpm_of_the_fc_with_largest_amount_of_scintillator_AT-id",
      },
      mass_with_greatest_amount_of_scintillator: {
        label:
          "Volumen de la fuente de calibración a espesor cero. Vfc (ml)mfc extintor=0 (mg)",
        decimals: 2,
        type: LabelTypesOptions.NUMERICO,
        order: 9,
        id: "mass_with_greatest_amount_of_scintillator_AT-id",
      },
      inc_mass_with_greatest_amount_of_scintillator: {
        label:
          "Inc. del volumen de la fuente de calibración a espesor cero uVfc (ml) (K=1)",
        decimals: 3,
        type: LabelTypesOptions.NUMERICO,
        order: 10,
        id: "inc_mass_with_greatest_amount_of_scintillator-id",
      },
      secondary_pattern_activity_of_the_fc_with_the_greatest_amount: {
        label:
          "Actividad patrón secundario de la fuente de calibración a espesor cero. (dpm)",
        decimals: 3,
        type: LabelTypesOptions.NUMERICO,
        order: 12,
        id: "secondary_pattern_activity_of_the_fc_with_the_greatest_amount_AT-id",
      },
      inc_squared_of_the_activity_of_the_secondary_standard: {
        label:
          "Inc. al cuadrado de la actividad del patrón secundario de la fc a espesor cero: u2dpmfc e=0 (k=1)",
        decimals: 3,
        type: LabelTypesOptions.NUMERICO,
        order: 13,
        id: "inc_squared_of_the_activity_of_the_secondary_standard-id",
      },
    },
    [KeysTabsAnalysis.AT_PP]: {
      cpm_of_the_fc_with_largest_amount_of_scintillator: {
        label:
          "cpm de la fuente de calibración a espesor cero: U2cpmfc e=0 (K=1)",
        decimals: 2,
        type: LabelTypesOptions.NUMERICO,
        order: 10,
        id: "cpm_of_the_fc_with_largest_amount_of_scintillator_AT-id",
      },
      mass_with_greatest_amount_of_scintillator: {
        label:
          "Volumen de la fuente de calibración a espesor cero. Vfc (ml)mfc extintor=0 (mg)",
        decimals: 2,
        type: LabelTypesOptions.NUMERICO,
        order: 9,
        id: "mass_with_greatest_amount_of_scintillator_AT-id",
      },
      inc_mass_with_greatest_amount_of_scintillator: {
        label:
          "Incertidumbre del volumen de la fuente de calibración a espesor cero uVfc (ml) (K=1)",
        decimals: 3,
        type: LabelTypesOptions.NUMERICO,
        order: 10,
        id: "inc_mass_with_greatest_amount_of_scintillator-id",
      },
      secondary_pattern_activity_of_the_fc_with_the_greatest_amount: {
        label:
          "Actividad patrón secundario de la fuente de calibración a espesor cero: dpmfc e=0",
        decimals: 3,
        type: LabelTypesOptions.NUMERICO,
        order: 12,
        id: "secondary_pattern_activity_of_the_fc_with_the_greatest_amount_AT-id",
      },
      inc_squared_of_the_activity_of_the_secondary_standard: {
        label:
          "Incertidumbre al cuadrado de la actividad del patrón secundario de la fc a espesor cero: u2dpmfc e=0 (k=1)",
        decimals: 3,
        type: LabelTypesOptions.NUMERICO,
        order: 13,
        id: "inc_squared_of_the_activity_of_the_secondary_standard-id",
      },
    },
    [KeysTabsAnalysis.BTBR]: {
      efficiency_y_90: {
        label: "Eficiencia k-40 de la muestra",
        decimals: 3,
        type: LabelTypesOptions.NUMERICO,
        order: 1,
        id: "efficiency_y_90-id",
      },
      cpm_fondo: {
        label: "cpm fondo alfa",
        decimals: 3,
        type: LabelTypesOptions.NUMERICO,
        order: 2,
        id: "cpm_fondo-id",
      },
      mass_with_greatest_amount_of_scintillator: {
        label:
          "Masa de la torta de la fuente de calibración de K con menor espesor, mfc KCl=min (mg)",
        decimals: 2,
        type: LabelTypesOptions.NUMERICO,
        order: 10,
        id: "mass_with_greatest_amount_of_scintillator-id",
      },
      inc_mass_with_greatest_amount_of_scintillator: {
        label:
          "Incertidumbre al cuadrado de la masa de la fuente de calibración de K con menor espesor, u2(mfc)KCl=min (K=1)",
        decimals: 3,
        type: LabelTypesOptions.NUMERICO,
        order: 11,
        id: "inc_mass_with_greatest_amount_of_scintillator-id",
      },
      calibration_source_cpm: {
        label:
          "cpm de la fuente de calibración con menor cantidad de KCI: cpm fc KCI=min",
        decimals: 2,
        type: LabelTypesOptions.NUMERICO,
        order: 10,
        id: "calibration_source_cpm-id",
      },
      inc_count_squared_fc_with_greater_amount_of_scintillator: {
        label:
          "Incertidumbre al cuadrado de las cpm de la fuente de calibración con menor cantidad KCl: U2cpm fc KCI=min",
        decimals: 3,
        type: LabelTypesOptions.NUMERICO,
        order: 13,
        id: "inc_count_squared_fc_with_greater_amount_of_scintillator-id",
      },
      calibration_source_count_rate_uncertainty_squared: {
        label:
          "Tasa de recuento del k-40 por unidad de volumen o de masa: r k40 (cpm/m3 o kg)",
        decimals: 3,
        type: LabelTypesOptions.CIENTIFICO,
        order: 28,
        id: "calibration_source_count_rate_uncertainty_squared-id",
      },
      inc_squared_activity_source_calibration_without_agent_extintor: {
        label:
          "Incertidumbre al cuadrado de la tasa de recuento del k-40 por unidad de volumen o de masa: U2r k40 (K=1)",
        decimals: 3,
        type: LabelTypesOptions.CIENTIFICO,
        order: 28,
        id: "inc_squared_activity_source_calibration_without_agent_extintor-id",
      },
    },
    [KeysTabsAnalysis.BT]: {
      cpm_fondo: {
        label: "cpm fondo alfa",
        decimals: 3,
        type: LabelTypesOptions.NUMERICO,
        order: 2,
        id: "cpm_fondo-id",
      },
      inc_of_calibration_source_volume_at_0: {
        label:
          "Volumen del patrón secundario en la fuente de calibración a espesor cero: Vpsfc (ml)",
        decimals: 3,
        type: LabelTypesOptions.NUMERICO,
        order: 9,
        id: "inc_of_calibration_source_volume_at_0-id",
      },
      secondary_pattern_activity_of_the_fc_with_the_greatest_amount: {
        label:
          "Actividad patrón secundario de la fuente de calibración a espesor cero (dpm): Act. dpm psfc (K=1)",
        decimals: 3,
        type: LabelTypesOptions.NUMERICO,
        order: 14,
        id: "secondary_pattern_activity_of_the_fc_with_the_greatest_amount-id",
      },
      inc_squared_of_the_activity_of_the_secondary_standard: {
        label:
          "Incertidumbre al cuadrado de la actividad del patrón secundario a espesor cero: U2cpmfc e=0 (K=1)",
        decimals: 3,
        type: LabelTypesOptions.NUMERICO,
        order: 15,
        id: "inc_squared_of_the_activity_of_the_secondary_standard-id",
      },
      count_uncertainty_squared: {
        label: "Incertidumbre al cuadrado de recuento: U2(rt) (K=1)",
        decimals: 3,
        type: LabelTypesOptions.CIENTIFICO,
        order: 18,
        id: "count_uncertainty_squared-id",
      },
      critical_limit: {
        label: "Límite Critico BT(Aβt*)",
        decimals: 3,
        type: LabelTypesOptions.CIENTIFICO,
        order: 21,
        id: "critical_limit-id",
      },
      secondary_pattern_mass: {
        label: "Volumen del patrón secundario: Vps (ml)",
        decimals: 2,
        type: LabelTypesOptions.NUMERICO,
        order: 8,
        id: "secondary_pattern_mass-id",
      },
      secondary_pattern_mass_uncertainty: {
        label: "Inc. del volumen del patrón secundario (mg): umps (k=1)",
        decimals: 3,
        type: LabelTypesOptions.NUMERICO,
        order: 9,
        id: "secondary_pattern_mass_uncertainty-id",
      },
      squared_counting_uncertainty_of_calibration_source_at_0: {
        label:
          "Incertidumbre volumen del patrón secundario en la fuente de calibración a espesor cero (ml) UVpsfc (K=1)",
        decimals: 3,
        type: LabelTypesOptions.NUMERICO,
        order: 11,
        id: "squared_counting_uncertainty_of_calibration_source_at_0-id",
      },
      cpm_of_the_fc_with_largest_amount_of_scintillator: {
        label:
          "cpm de la fuente de calibración a espesor cero: cpmfc e=0 (K=1)",
        decimals: 2,
        type: LabelTypesOptions.NUMERICO,
        order: 12,
        id: "cpm_of_the_fc_with_largest_amount_of_scintillator-id",
      },
      inc_count_squared_fc_without_agent_extintor: {
        label:
          "Incertidumbre de recuento al cuadrado de la fuente de calibración a espesor cero: U2cpmfc e=0 (K=1)",
        decimals: 3,
        type: LabelTypesOptions.NUMERICO,
        order: 13,
        id: "inc_count_squared_fc_without_agent_extintor-id",
      },
      inverse_squared_uncertainty: {
        label:
          "Inc. al cuadrado de la inversa de la eficiencia de la muestra: u2W (k=1)",
        decimals: 3,
        type: LabelTypesOptions.CIENTIFICO,
        order: 16,
        id: "inverse_squared_uncertainty-id",
      },
    },
    [KeysTabsAnalysis.BR]: {
      cpm_fondo: {
        label: "cpm fondo alfa",
        decimals: 3,
        type: LabelTypesOptions.NUMERICO,
        order: 2,
        id: "cpm_fondo-id",
      },
      inc_of_calibration_source_volume_at_0: {
        label:
          "Volumen del patrón secundario en la fuente de calibración a espesor cero: Vpsfc (ml)",
        decimals: 3,
        type: LabelTypesOptions.NUMERICO,
        order: 10,
        id: "inc_of_calibration_source_volume_at_0-id",
      },
      secondary_pattern_activity_of_the_fc_with_the_greatest_amount: {
        label:
          "Actividad patrón secundario de la fuente de calibración a espesor cero (dpm): Act. dpm psfc (K=1)",
        decimals: 3,
        type: LabelTypesOptions.NUMERICO,
        order: 14,
        id: "secondary_pattern_activity_of_the_fc_with_the_greatest_amount-id",
      },
      inc_squared_of_the_activity_of_the_secondary_standard: {
        label:
          "Incertidumbre al cuadrado de la actividad del patrón secundario a espesor cero: U2cpmfc e=0 (K=1)",
        decimals: 3,
        type: LabelTypesOptions.NUMERICO,
        order: 15,
        id: "inc_squared_of_the_activity_of_the_secondary_standard-id",
      },
      count_uncertainty_squared: {
        label: "Incertidumbre al cuadrado de recuento: U2(rt) (K=1)",
        decimals: 3,
        type: LabelTypesOptions.CIENTIFICO,
        order: 18,
        id: "count_uncertainty_squared-id",
      },
      critical_limit: {
        label: "Límite Critico BT(Aβt*)",
        decimals: 3,
        type: LabelTypesOptions.CIENTIFICO,
        order: 21,
        id: "critical_limit-id",
      },
      cpm_of_the_fc_with_largest_amount_of_scintillator: {
        label:
          "cpm de la fuente de calibración a espesor cero: cpmfc e=0 (K=1)",
        decimals: 2,
        type: LabelTypesOptions.NUMERICO,
        order: 12,
        id: "cpm_of_the_fc_with_largest_amount_of_scintillator-id",
      },
      inc_count_squared_fc_without_agent_extintor: {
        label:
          "Incertidumbre de recuento al cuadrado de la fuente de calibración a espesor cero: U2cpmfc e=0 (K=1)",
        decimals: 3,
        type: LabelTypesOptions.NUMERICO,
        order: 13,
        id: "inc_count_squared_fc_without_agent_extintor-id",
      },
      efficiency_y_90: {
        label: "Eficiencia k-40 de la muestra",
        decimals: 3,
        type: LabelTypesOptions.NUMERICO,
        order: 24,
        id: "efficiency_y_90-id",
      },
      mass_with_greatest_amount_of_scintillator: {
        label:
          "Masa de la torta de la fuente de calibración de K con menor espesor, mfc KCl=min (mg)",
        decimals: 2,
        type: LabelTypesOptions.NUMERICO,
        order: 25,
        id: "mass_with_greatest_amount_of_scintillator-id",
      },
      inc_mass_with_greatest_amount_of_scintillator: {
        label:
          "Inc. al cuadrado de la masa de la fuente de calibración de K con menor espesor, u2(mfc)KCl=min (K=1)",
        decimals: 3,
        type: LabelTypesOptions.NUMERICO,
        order: 26,
        id: "inc_mass_with_greatest_amount_of_scintillator-id",
      },
      secondary_pattern_mass: {
        label: "Volumen del patrón secundario: Vps (ml)",
        decimals: 2,
        type: LabelTypesOptions.NUMERICO,
        order: 8,
        id: "secondary_pattern_mass-id",
      },
      secondary_pattern_mass_uncertainty: {
        label: "Inc. del volumen del patrón secundario (mg): umps (k=1)",
        decimals: 3,
        type: LabelTypesOptions.NUMERICO,
        order: 9,
        id: "secondary_pattern_mass_uncertainty-id",
      },
      squared_counting_uncertainty_of_calibration_source_at_0: {
        label:
          "Incertidumbre volumen del patrón secundario en la fuente de calibración a espesor cero (ml) UVpsfc (K=1)",
        decimals: 3,
        type: LabelTypesOptions.NUMERICO,
        order: 11,
        id: "squared_counting_uncertainty_of_calibration_source_at_0-id",
      },
      inverse_squared_uncertainty: {
        label:
          "Inc. al cuadrado de la inversa de la eficiencia de la muestra: u2W (k=1)",
        decimals: 3,
        type: LabelTypesOptions.CIENTIFICO,
        order: 16,
        id: "inverse_squared_uncertainty-id",
      },
      calibration_source_cpm: {
        label:
          "cpm de la fuente de calibración con menor cantidad de KCI: cpm fc KCI=min",
        decimals: 2,
        type: LabelTypesOptions.NUMERICO,
        order: 27,
        id: "calibration_source_cpm-id",
      },
      inc_count_squared_fc_with_greater_amount_of_scintillator: {
        label:
          "Incertidumbre al cuadrado de las cpm de la fuente de calibración con menor cantidad KCl: U2cpm fc KCI=min",
        decimals: 3,
        type: LabelTypesOptions.NUMERICO,
        order: 28,
        id: "inc_count_squared_fc_with_greater_amount_of_scintillator-id",
      },
      inc_squared_activity_source_calibration_without_agent_extintor: {
        label:
          "Incertidumbre al cuadrado de la tasa de recuento del k-40 por unidad de volumen o de masa: U2r k40 (K=1)",
        decimals: 3,
        type: LabelTypesOptions.CIENTIFICO,
        order: 35,
        id: "inc_squared_activity_source_calibration_without_agent_extintor-id",
      },
    },
    [KeysTabsAnalysis.BPP]: {
      cpm_fondo: {
        label: "cpm fondo alfa",
        decimals: 3,
        type: LabelTypesOptions.NUMERICO,
        order: 2,
        id: "cpm_fondo-id",
      },
    },
    [KeysTabsAnalysis.Pu238]: {
      efficiency: {
        label: "Eficiencia total",
        decimals: 4,
        type: LabelTypesOptions.NUMERICO,
        order: 1,
        id: "efficiency-id",
      },
      efficiency_sr_89: {
        label: "Eficiencia detector",
        decimals: 4,
        type: LabelTypesOptions.NUMERICO,
        order: 2,
        id: "efficiency_sr_89-id",
      },
      inc_rel_squared_performance: {
        label: "Rendimiento (%)",
        decimals: 2,
        type: LabelTypesOptions.NUMERICO,
        order: 3,
        id: "inc_rel_squared_performance-id",
      },
    },
    [KeysTabsAnalysis.Pu239_240]: {
      efficiency: {
        label: "Eficiencia total",
        decimals: 4,
        type: LabelTypesOptions.NUMERICO,
        order: 1,
        id: "efficiency-id",
      },
      efficiency_sr_89: {
        label: "Eficiencia detector",
        decimals: 4,
        type: LabelTypesOptions.NUMERICO,
        order: 2,
        id: "efficiency_sr_89-id",
      },
      inc_rel_squared_performance: {
        label: "Rendimiento (%)",
        decimals: 2,
        type: LabelTypesOptions.NUMERICO,
        order: 3,
        id: "inc_rel_squared_performance-id",
      },
    },
    [KeysTabsAnalysis.AM241]: {
      efficiency: {
        label: "Eficiencia total",
        decimals: 4,
        type: LabelTypesOptions.NUMERICO,
        order: 1,
        id: "efficiency-id",
      },
      efficiency_sr_89: {
        label: "Eficiencia detector",
        decimals: 4,
        type: LabelTypesOptions.NUMERICO,
        order: 2,
        id: "efficiency_sr_89-id",
      },
      inc_rel_squared_performance: {
        label: "Rendimiento (%)",
        decimals: 2,
        type: LabelTypesOptions.NUMERICO,
        order: 3,
        id: "inc_rel_squared_performance-id",
      },
    },
    [KeysTabsAnalysis.Cm243_244]: {
      efficiency: {
        label: "Eficiencia total",
        decimals: 4,
        type: LabelTypesOptions.NUMERICO,
        order: 1,
        id: "efficiency-id",
      },
      efficiency_sr_89: {
        label: "Eficiencia detector",
        decimals: 4,
        type: LabelTypesOptions.NUMERICO,
        order: 2,
        id: "efficiency_sr_89-id",
      },
      inc_rel_squared_performance: {
        label: "Rendimiento (%)",
        decimals: 2,
        type: LabelTypesOptions.NUMERICO,
        order: 3,
        id: "inc_rel_squared_performance-id",
      },
    },
    [KeysTabsAnalysis.Fe55]: {
      mass_with_greatest_amount_of_scintillator: {
        label:
          "Masa de fuente de calibración sin agente extintor. mfc extintor=0 (mg)",
        decimals: 2,
        type: LabelTypesOptions.NUMERICO,
        order: 10,
        id: "mass_with_greatest_amount_of_scintillator-id",
      },
      cpm_of_the_fc_with_largest_amount_of_scintillator: {
        label:
          "cpm de fuente de calibración sin agente extintor: cpmfc extintor=0 ",
        decimals: 2,
        type: LabelTypesOptions.NUMERICO,
        order: 13,
        id: "cpm_of_the_fc_with_largest_amount_of_scintillator-id",
      },
      uncertainty_squared_relative_inverse_sample_efficiency: {
        label:
          "Inc. al cuadrado relativa de la inversa de la eficiencia de la muestra: u2relWmuestra(K=1)",
        decimals: 3,
        type: LabelTypesOptions.CIENTIFICO,
        order: 17,
        id: "uncertainty_squared_relative_inverse_sample_efficiency-id",
      },
      inc_rel_squared_sample_vial_quantity: {
        label:
          "Inc. relativa al cuadrado de la cantidad de muestra vial: u2Cant.muestra vial (K=1)",
        decimals: 3,
        type: LabelTypesOptions.CIENTIFICO,
        order: 18,
        id: "inc_rel_squared_sample_vial_quantity-id",
      },
      inc_rel_squared_performance: {
        label: "Inc. relativa al cuadrado del rendimiento. u2relRto(K=1)",
        decimals: 3,
        type: LabelTypesOptions.CIENTIFICO,
        order: 19,
        id: "inc_rel_squared_performance-id",
      },
      k40_relative_uncertainty: {
        label:
          "Incertidumbre relativa al cuadrado del factor de auto absorción en la calibración del k-40: U2rel Fa k40 (K=1)",
        decimals: 3,
        type: LabelTypesOptions.CIENTIFICO,
        order: 20,
        id: "k40_relative_uncertainty-id",
      },
    },
    [KeysTabsAnalysis.Ni63]: {
      mass_with_greatest_amount_of_scintillator: {
        label:
          "Masa de fuente de calibración sin agente extintor. mfc extintor=0 (mg)",
        decimals: 2,
        type: LabelTypesOptions.NUMERICO,
        order: 10,
        id: "mass_with_greatest_amount_of_scintillator-id",
      },
      cpm_of_the_fc_with_largest_amount_of_scintillator: {
        label:
          "cpm de fuente de calibración sin agente extintor: cpmfc extintor=0 ",
        decimals: 2,
        type: LabelTypesOptions.NUMERICO,
        order: 13,
        id: "cpm_of_the_fc_with_largest_amount_of_scintillator-id",
      },
      uncertainty_squared_relative_inverse_sample_efficiency: {
        label:
          "Inc. al cuadrado relativa de la inversa de la eficiencia de la muestra: u2relWmuestra(K=1)",
        decimals: 3,
        type: LabelTypesOptions.CIENTIFICO,
        order: 17,
        id: "uncertainty_squared_relative_inverse_sample_efficiency-id",
      },
      inc_rel_squared_sample_vial_quantity: {
        label:
          "Inc. relativa al cuadrado de la cantidad de muestra vial: u2Cant.muestra vial (K=1)",
        decimals: 3,
        type: LabelTypesOptions.CIENTIFICO,
        order: 18,
        id: "inc_rel_squared_sample_vial_quantity-id",
      },
      inc_rel_squared_performance: {
        label: "Inc. relativa al cuadrado del rendimiento. u2relRto(K=1)",
        decimals: 3,
        type: LabelTypesOptions.CIENTIFICO,
        order: 19,
        id: "inc_rel_squared_performance-id",
      },
    },
    [KeysTabsAnalysis.AT_C]: {
      mass_with_greatest_amount_of_scintillator: {
        label:
          "Masa de la f.c. con menor cantidad portador de Fe y Ba, mfc (mg)",
        decimals: 2,
        type: LabelTypesOptions.NUMERICO,
        order: 10,
        id: "mass_with_greatest_amount_of_scintillator_AT-id",
      },
      inc_mass_with_greatest_amount_of_scintillator: {
        label:
          "Incertidumbre de la masa de la f.c. con menor cantidad Fe y Ni. umfc (mg) (K=1)",
        decimals: 3,
        type: LabelTypesOptions.NUMERICO,
        order: 11,
        id: "inc_mass_with_greatest_amount_of_scintillator-id",
      },
      cpm_of_the_fc_with_largest_amount_of_scintillator: {
        label:
          "cpm de la f.c. con menor cantidad de Fe y Ni: u2cpmfc e=0 (K=1)",
        decimals: 2,
        type: LabelTypesOptions.NUMERICO,
        order: 11,
        id: "cpm_of_the_fc_with_largest_amount_of_scintillator_AT-id",
      },
      squared_counting_uncertainty_of_calibration_source_at_0: {
        label:
          "Incertidumbre de recuento al cuadrado de la f.c. con menor cantidad de Fe y Ni: u2cpmfc e=0 (K=1)",
        decimals: 3,
        type: LabelTypesOptions.NUMERICO,
        order: 12,
        id: "squared_counting_uncertainty_of_calibration_source_at_0-id",
      },
      secondary_pattern_activity_of_the_fc_with_the_greatest_amount: {
        label:
          "Actividad patrón secundario de la f.c. con menor cantidad de Fe y Ba. (dpm) ",
        decimals: 3,
        type: LabelTypesOptions.NUMERICO,
        order: 13,
        id: "secondary_pattern_activity_of_the_fc_with_the_greatest_amount_AT-id",
      },
      inc_squared_of_the_activity_of_the_secondary_standard: {
        label:
          "Inc. al cuadrado de la actividad del patrón secundario con menor cantidad de Fe y Ba: u2dpmfc e=0 (K=1)",
        decimals: 3,
        type: LabelTypesOptions.NUMERICO,
        order: 14,
        id: "inc_squared_of_the_activity_of_the_secondary_standard-id",
      },
    },
  };

  // Crear una copia de labelString y sobrescribir elementos según el análisis
  const extendedLabelString = {
    ...labelString,
    ...(overrideLabels[analisisTypeCode] || {}),
  };

  // Mapea las claves de data si es necesario
  const transformedData = Object.entries(data).reduce((acc, [key, value]) => {
    const newKey =
      analisisTypeCode === KeysTabsAnalysis.AT &&
      key === "cpm_of_the_fc_with_largest_amount_of_scintillator"
        ? `${key}_AT`
        : key === "mass_with_greatest_amount_of_scintillator" &&
          analisisTypeCode === KeysTabsAnalysis.AT
        ? `${key}_AT`
        : key ===
            "secondary_pattern_activity_of_the_fc_with_the_greatest_amount" &&
          analisisTypeCode === KeysTabsAnalysis.AT
        ? `${key}_AT`
        : key === "efficiency_sr_89" &&
          analisisTypeCode === KeysTabsAnalysis.Pu238
        ? `${key}`
        : key;

    acc[newKey] = value;
    return acc;
  }, {} as Record<string, T>);

  const idsToCheck =
    analisisTypeCode === KeysTabsAnalysis.BR && !isCurrier
      ? idsBRISO
      : analisisTypeCode === KeysTabsAnalysis.BR && isCurrier
      ? idsBRCurrier
      : analisisTypeCode === KeysTabsAnalysis.BT && !isCurrier
      ? idsBTISO
      : analisisTypeCode === KeysTabsAnalysis.BT && isCurrier
      ? idsBTCurrier
      : analisisTypeCode === KeysTabsAnalysis.BTBR && !isCurrier
      ? idsBTBRInSample
      : analisisTypeCode === KeysTabsAnalysis.BTBR && isCurrier
      ? idsBTBRInSampleCurrier
      : isCurrier
      ? idsCurries
      : analisisTypeCode === KeysTabsAnalysis.Fe55 ||
        analisisTypeCode === KeysTabsAnalysis.Ni63
      ? idsFe55Ni63
      : analisisTypeCode === KeysTabsAnalysis.AT_E ||
        analisisTypeCode === KeysTabsAnalysis.AT_C ||
        analisisTypeCode === KeysTabsAnalysis.AT_PP
      ? idsAT
      : analisisTypeCode === KeysTabsAnalysis.DP
      ? idsDP
      : analisisTypeCode === KeysTabsAnalysis.I131
      ? idsIodo
      : analisisTypeCode === KeysTabsAnalysis.Sr89 ||
        analisisTypeCode === KeysTabsAnalysis.Sr90 ||
        analisisTypeCode === KeysTabsAnalysis.Sr89_90
      ? idsSR
      : analisisTypeCode === KeysTabsAnalysis.BPP
      ? idsBTBRCurrier
      : analisisTypeCode === KeysTabsAnalysis.Pu238 ||
        analisisTypeCode === KeysTabsAnalysis.Pu239_240 ||
        analisisTypeCode === KeysTabsAnalysis.AM241 ||
        analisisTypeCode === KeysTabsAnalysis.Cm243_244
      ? idsPuAmCM
      : idsDefault;

  const arrrayKeys = Object.entries(transformedData)
    .filter(([key]) => key in extendedLabelString)
    .filter(([key]) =>
      idsToCheck.includes(extendedLabelString[key as LabelKeys].id)
    )
    .sort(
      ([keyA], [keyB]) =>
        extendedLabelString[keyA].order - extendedLabelString[keyB].order
    );

  return { arrrayKeys, extendedLabelString };
}

export function generateSampleFlow(sample, stateSample, preparation) {
  const flowSample: any = [];
  const flowInitial: any = [];
  const flowAnalysis: any = [];
  const performanceTable: any = [];

  let dateFromPerformance = null;
  let dateToPerformance = null;

  // Alta muestra
  flowSample.push({
    id: sample.id,
    fase: "Alta muestra",
    analyst: sample.analyst?.username,
    date: sample.high_date,
  });
  if (stateSample.sampling?.current) {
    // Toma de muestra
    flowInitial.push({
      id: stateSample.sampling?.current?.id,
      fase: "Toma de muestra",
      analyst: stateSample.sampling?.current?.analyst?.username,
      date: stateSample.sampling?.current?.date_start_sampling,
    });
  }

  if (stateSample.management?.current) {
    // Gestión de muestra
    flowInitial.push({
      id: stateSample.management?.current?.id,
      fase: "Gestión de muestra - Recepción",
      analyst: stateSample.management?.current?.reception_analyst?.username,
      date: stateSample.management?.current?.date_start_reception,
    });
    if (stateSample.management?.current?.retirement_date) {
      flowInitial.push({
        id: `${stateSample.management?.current?.id}-retirement`,
        fase: "Gestión de muestra - Retirada",
        analyst: stateSample.management?.current?.retirement_analyst?.username,
        date: stateSample.management?.current?.retirement_date,
      });
    }
    //determinar fecha de inicio de rendimiento
    if (stateSample.sampling?.current?.type_sampler === "MASL") {
      dateFromPerformance = stateSample.sampling?.current?.date_start_sampling;
    } else {
      dateFromPerformance =
        stateSample.management?.current?.date_start_reception;
    }
  }
  if (stateSample.conditioning?.current) {
    // Acondicionamiento
    flowInitial.push({
      id: stateSample.conditioning?.current?.id,
      fase: "Acondicionamiento",
      analyst: stateSample.conditioning?.current?.analyst?.username,
      date: stateSample.conditioning?.current?.date_time,
    });
  }
  if (stateSample.processing?.current) {
    // Procesamiento
    stateSample.processing?.current?.forEach((proc) => {
      flowInitial.push({
        id: proc.id,
        fase: `Procesamiento ${proc?.processing_category?.name}`,
        analyst: proc.analyst?.username,
        date: proc.date_time,
      });
    });
  }

  // Preparación y análisis agrupados por analysis_type[0].id
  preparation.forEach((prep) => {
    const analysisId = prep.analysis_type[0]?.id || "unknown";

    // Buscar si ya existe un grupo para este análisis
    let group = flowAnalysis.find((item) => item.analysisId === analysisId);

    // Si no existe, crearlo
    if (!group) {
      group = {
        analysisId,
        analysisDescription:
          prep.analysis_type[0]?.descripcion || "Sin descripción",
        analysisCode: prep.analysis_type[0]?.codigo || "Sin código",
        data: [],
      };
      flowAnalysis.push(group);
    }

    // Crear estructura de la preparación
    const preparationData: any = {
      id: prep.id,
      fase: `Preparación`,
      analyst: prep.analyst?.username || "Sin asignar",
      date: prep.start_date || "Fecha no disponible",
      measures: [],
      countings: [],
    };

    // Agregar medidas (aa_measures) a la preparación
    prep.aa_measures?.forEach((measure) => {
      const measureData: any = {
        id: measure.id,
        fase: `Medida`,
        analyst: measure.analyst?.username || "Sin asignar",
        date: measure.start_date_time || "Fecha no disponible",
        countings: [],
      };

      // Contajes y cálculos de la medida
      measure.countings?.forEach((counting) => {
        const countingData: any = {
          id: counting.id,
          fase: `Contaje`,
          analyst: counting.analyst?.username || "Sin asignar",
          date: counting.counting_date || "Fecha no disponible",
          calculations: [],
        };

        // Agregar cálculos al contaje
        counting.calculations?.forEach((calc) => {
          const calcDate = new Date(calc.calculation_date);
          countingData.calculations.push({
            id: calc.id,
            fase: `Cálculo`,
            analyst: calc.analyst?.username || "Sin asignar",
            date: calc.calculation_date || "Fecha no disponible",
          });

          // Actualizar dateToPerformance con la fecha más alta
          if (!dateToPerformance || calcDate > new Date(dateToPerformance)) {
            dateToPerformance = calc.calculation_date;
          }
        });

        measureData.countings.push(countingData);
      });

      preparationData.measures.push(measureData);
    });

    // Contajes y cálculos sin medidas (aa_measures)
    prep.countings?.forEach((counting) => {
      const countingData: any = {
        id: counting.id,
        fase: `Contaje`,
        analyst: counting.analyst?.username || "Sin asignar",
        date: counting.counting_date || "Fecha no disponible",
        calculations: [],
      };

      // Agregar cálculos al contaje
      counting.calculations?.forEach((calc) => {
        const calcDate = new Date(calc.calculation_date);

        countingData.calculations.push({
          id: calc.id,
          fase: `Cálculo`,
          analyst: calc.analyst?.username || "Sin asignar",
          date: calc.calculation_date || "Fecha no disponible",
        });

        // Actualizar dateToPerformance con la fecha más alta
        if (!dateToPerformance || calcDate > new Date(dateToPerformance)) {
          dateToPerformance = calc.calculation_date;
        }
      });

      preparationData.countings.push(countingData);
    });

    // Agregar preparación al grupo correspondiente
    group.data.push(preparationData);
  });

  // Calcular rendimiento en días con 2 decimales
  const performance =
    dateToPerformance && dateFromPerformance
      ? parseFloat(
          (
            (new Date(dateToPerformance).getTime() -
              new Date(dateFromPerformance).getTime()) /
            (1000 * 60 * 60 * 24)
          ).toFixed(2)
        )
      : null;

  if (dateToPerformance && dateFromPerformance) {
    performanceTable.push({
      id: `performance-${sample.id}`,
      dateFrom: dateFromPerformance,
      dateTo: dateToPerformance,
      performance: performance,
    });
  }

  return { flowSample, flowInitial, flowAnalysis, performanceTable };
}

export function parseDateTime(dateTimeStr) {
  if (!dateTimeStr) return null;
  dateTimeStr = dateTimeStr.trim();
  let [datePart, timePart] = dateTimeStr.split(/\s+/);
  let [day, month, year] = datePart.split("/").map(Number);
  year += year < 50 ? 2000 : 1900;
  let [hours, minutes, seconds] = timePart.split(":").map(Number);
  return new Date(year, month - 1, day, hours, minutes, seconds);
}

export function parseDate(dateTimeStr) {
  if (!dateTimeStr) return null;
  dateTimeStr = dateTimeStr.trim();
  let [datePart] = dateTimeStr.split(/\s+/);
  let [day, month, year] = datePart.split("/").map(Number);
  return new Date(year, month - 1, day + 1);
}

export function parseIsotopeData(
  data,
  isotopes,
  efficiencies,
  filterFondos,
  counting_time,
  count_351,
  count_911,
  calculation_self_absorption_factor,
  calculation_geometric_factor,
  sample_amount,
  diffDate,
  performance,
  cod_instalacion,
  ref_cliente,
  geometry,
  equipment,
  counting_date
) {
  return data
    .trim()
    .split("\n")
    .map((line, index) => {
      const parts = line.split("|");
      if (parts[0] !== cod_instalacion + ref_cliente) return;
      const isotopoFile = parts[1] + parts[2].trim();
      const cuenta = parseFloat(parts[3] + "E" + parts[4]);
      const error = parseFloat(parts[5]);
      const isotopeMatch = isotopes.find(
        (iso) => iso.codigo.toUpperCase() === isotopoFile.toUpperCase()
      );
      const energy = isotopeMatch ? parseFloat(isotopeMatch.energia) : 0;
      const factor351 = isotopeMatch ? parseFloat(isotopeMatch.factor351) : 0;
      const factor911 = isotopeMatch ? parseFloat(isotopeMatch.factor911) : 0;
      const cdesc = isotopeMatch ? parseFloat(isotopeMatch.cdes) : 0;
      const gammades = isotopeMatch ? parseFloat(isotopeMatch.gammades) : 0;
      const sortedEfficiencies = efficiencies.sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());

      const matchingEfficiency = sortedEfficiencies.find(
        (eff) => 
          typeof eff.efficiencies_isotopes !== "undefined" &&
          eff.cs_source_geometry?.codigo && 
          geometry?.codigo?.replace(/\d+/g, '').includes(eff.cs_source_geometry.codigo.replace(/\d+/g, '')) &&
          eff.equipment?.codigo === equipment?.codigo &&
          eff.date <= counting_date
      );
      
      let efficiency = 0;
      if (matchingEfficiency) {
        const efficiencie_low = matchingEfficiency.efficiencies_isotopes.find((ei) => ei.is_low === true);
        const efficiencie_high = matchingEfficiency.efficiencies_isotopes.find((ei) => ei.is_low === false);
        if( energy <= 165.9 ){ //low energy
          efficiency = Math.exp( Math.log(energy) ** 4 * parseFloat(efficiencie_low.x4) + Math.log(energy) ** 3 * parseFloat(efficiencie_low.x3) + Math.log(energy) ** 2 * parseFloat(efficiencie_low.x2) + Math.log(energy) * parseFloat(efficiencie_low.x) + parseFloat(efficiencie_low.a) );
        }else{ //high energy
          efficiency = Math.exp( Math.log(energy) ** 4 * parseFloat(efficiencie_high.x4) + Math.log(energy) ** 3 * parseFloat(efficiencie_high.x3) + Math.log(energy) ** 2 * parseFloat(efficiencie_high.x2) + Math.log(energy) * parseFloat(efficiencie_high.x) + parseFloat(efficiencie_high.a) );
        }
      }

      let fondo_area_to_apply = 0;
      let fondo_area_error_to_apply = 0;
      let fondo_integration_to_apply = 0;

      const fondoMatch = filterFondos.find(
        (fondo) =>
          fondo.equipment?.codigo === equipment?.codigo &&
          fondo.calculation_date <= counting_date &&
          fondo.fondo_calculation &&
          fondo.fondo_calculation.some(
        (fc) =>
          fc.isotope_instalation.codigo.toUpperCase() ===
          isotopoFile.toUpperCase()
          )
      );

      if (fondoMatch) {
        const fondoCalculation = fondoMatch.fondo_calculation.find(
          (fc) =>
            fc.isotope_instalation.codigo.toUpperCase() ===
            isotopoFile.toUpperCase()
        );
        if (fondoCalculation) {
          fondo_area_to_apply =
            parseFloat(fondoCalculation.fondo_area_to_apply) || 0;
          fondo_area_error_to_apply =
            parseFloat(fondoCalculation.fondo_area_error_to_apply) || 0;
          fondo_integration_to_apply =
            parseFloat(fondoCalculation.fondo_integration_to_apply) || 0;
        }
      }
      
      const areaNeta =
        cuenta -
        fondo_area_to_apply * Number(counting_time) -
        factor351 * Number(count_351) -
        factor911 * Number(count_911);

      const efficiencyDenominador =
        Number(counting_time) *
        Number(efficiency) *
        Number(gammades) *
        (Number(calculation_self_absorption_factor) /
          Number(typeof calculation_geometric_factor === 'string' ? calculation_geometric_factor.replace(',', '.') : calculation_geometric_factor) || 1) *
        Number(sample_amount) *
        Number(performance);

      const exponencial = Math.exp(cdesc * Number(diffDate));

      let actividad = (areaNeta / efficiencyDenominador) * exponencial;
      if (!isFinite(actividad)) {
        actividad = 0;
      }

      const errorAreaCount = Math.sqrt(
        Math.pow((((cuenta * 100) / 90) * error) / 100, 2) +
          Math.pow(fondo_area_error_to_apply * Number(counting_time), 2)
      );

      let incertidumbre =
        ((2 * errorAreaCount) / efficiencyDenominador) * exponencial;

      if (!isFinite(incertidumbre)) {
        incertidumbre = 0;
      }

      let lid =
        (4.66 * Math.sqrt(fondo_integration_to_apply * Number(counting_time))) * exponencial /
        efficiencyDenominador;
      
      if (!isFinite(lid)) {
        lid = 0;
      }

      return {
        id: index,
        isotopoFile,
        cuenta,
        error,
        energy,
        factor351: factor351 || 0,
        factor911: factor911 || 0,
        gammades: gammades || 0,
        cdesc: cdesc || 0,
        efficiency: efficiency || 0,
        fondo_area_to_apply: fondo_area_to_apply || 0,
        fondo_area_error_to_apply: fondo_area_error_to_apply || 0,
        fondo_integration_to_apply: fondo_integration_to_apply || 0,
        actividad: actividad || 0,
        incertidumbre: incertidumbre || 0,
        lid: lid || 0,
      };
    });
}

export const formatValueWithCustomRounding = (
  value: any,
  type: string,
  decimals: number
): string => {
  const numericValue = parseFloat(value);
  if (isNaN(numericValue)) {
    return value;
  }

  const customRoundNumeric = (val: number, dec: number): number => {
    const multiplier = Math.pow(10, dec);
    const shifted = val * multiplier;
    const truncated = Math.floor(Math.abs(shifted));
    const decimal = Math.abs(shifted) - truncated;
    
    // Obtener el siguiente dígito
    const nextDigit = Math.floor(decimal * 10);
    
    // Si el siguiente dígito es 5, redondear hacia arriba
    if (nextDigit === 5) {
      return (val >= 0 ? Math.ceil(shifted) : Math.floor(shifted)) / multiplier;
    } else {
      return Math.round(shifted) / multiplier;
    }
  };

  const customRoundExponential = (val: number, dec: number): string => {
    if (val === 0) return "0E+0";
    
    const sign = val < 0 ? -1 : 1;
    const absValue = Math.abs(val);
    const exponent = Math.floor(Math.log10(absValue));
    const mantissa = absValue / Math.pow(10, exponent);
    
    // Redondeo manual para la mantissa
    const multiplier = Math.pow(10, dec);
    const shifted = mantissa * multiplier;
    const truncated = Math.floor(shifted);
    const decimal = shifted - truncated;
    const nextDigit = Math.floor(decimal * 10);
    
    let roundedMantissa;
    if (nextDigit >= 5) {
      // Si el siguiente dígito es 5 o mayor, redondear hacia arriba
      roundedMantissa = Math.ceil(shifted) / multiplier;
    } else {
      roundedMantissa = Math.floor(shifted) / multiplier;
    }
    
    // Aplicar el signo
    roundedMantissa = roundedMantissa * sign;
    
    return `${roundedMantissa.toFixed(dec)}E${exponent >= 0 ? '+' : ''}${exponent}`;
  };

  if (type === LabelTypesOptions.NUMERICO) {
    return customRoundNumeric(numericValue, decimals).toFixed(decimals);
  } else if (type === LabelTypesOptions.CIENTIFICO) {
    return customRoundExponential(numericValue, decimals);
  } else if (type === LabelTypesOptions.CIENTIFICOCONCERO) {
    return customRoundExponential(numericValue, 3);
  }
  
  return value;
};

// Función específica para formatear valores de Beta Resto (BR)
// Aplica truncamiento (no redondeo) solo a cpm_fondo y cpm_fondo_beta
export const formatBRValue = (
  value: any,
  type: string,
  decimals: number,
  fieldKey: string
): string => {
  // Solo aplicar truncamiento a cpm_fondo y cpm_fondo_beta
  if ((fieldKey === 'cpm_fondo' || fieldKey === 'cpm_fondo_beta') && type === LabelTypesOptions.NUMERICO) {
    const numericValue = parseFloat(value);
    if (isNaN(numericValue)) {
      return value;
    }
    
    // Truncar (recortar) el valor hasta el número de decimales especificado
    const multiplier = Math.pow(10, decimals);
    const shifted = numericValue * multiplier;
    const truncated = Math.trunc(shifted) / multiplier;
    
    return truncated.toFixed(decimals);
  }
  
  // Para otros campos, usar el formato normal
  return formatValueWithCustomRounding(value, type, decimals);
};
